import * as LucideIcons from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { type CMSAction, CMSActionService } from "@/services/cmsActionService";
import { type DataBinding, DataBindingService } from "@/services/dataBindingService";
import { CMSService } from "@/services/cmsService";
import { useEditorStore } from "@/store/editorStore";
import { sanitizeSvgMarkup } from "@/utils/svgSanitizer";
import { CollectionListRenderer } from "./CollectionListRenderer";
import { getParentPaddingForComponent, resolveComponentLayoutStyle } from "./skinLayoutResolver";

// Global flag to prevent camera movement during skin component resizing
let isSkinComponentResizingRef = false;
export const setIsSkinComponentResizing = (value: boolean) => {
  isSkinComponentResizingRef = value;
};
export const getIsSkinComponentResizing = () => isSkinComponentResizingRef;

const mergeTransforms = (
  base: React.CSSProperties["transform"],
  extra: string | null,
): React.CSSProperties["transform"] => {
  if (!extra) return base;
  if (!base || base === "none") return extra;
  return `${extra} ${base}`;
};

const resolveCssColorToRgb = (color: string): { r: number; g: number; b: number } | null => {
  if (typeof document === "undefined") return null;
  const temp = document.createElement("div");
  temp.style.color = color;
  temp.style.visibility = "hidden";
  temp.style.position = "absolute";
  temp.style.top = "-9999px";
  document.body.appendChild(temp);
  const computed = window.getComputedStyle(temp).color;
  document.body.removeChild(temp);
  const match = computed.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
  if (!match) return null;
  const [, r, g, b] = match;
  return { r: Number(r), g: Number(g), b: Number(b) };
};

const resolveColorMixValue = (value?: string): string | undefined => {
  if (!value) return value;
  const trimmed = value.trim();
  const mixMatch = trimmed.match(
    /color-mix\([^,]+,\s*([^,]+)\s+([0-9.]+)%\s*,\s*transparent\s*\)/i,
  );
  if (!mixMatch) return value;
  const baseColor = mixMatch[1].trim();
  const percent = Number.parseFloat(mixMatch[2]);
  if (!Number.isFinite(percent)) return value;
  const resolved = resolveCssColorToRgb(baseColor);
  if (!resolved) return value;
  const alpha = Math.max(0, Math.min(1, percent / 100));
  return `rgba(${resolved.r}, ${resolved.g}, ${resolved.b}, ${alpha})`;
};

interface SkinComponent {
  id: string;
  type: "box" | "text" | "image" | "icon" | "collection-list" | "tabs" | "dropdown";
  name: string;
  position: {
    left?: number | string;
    right?: number | string;
    top?: number | string;
    bottom?: number | string;
  };
  size: {
    width: number | string;
    height: number | string;
    minWidth?: number | string;
    minHeight?: number | string;
    maxWidth?: number | string;
    maxHeight?: number | string;
    aspectRatio?: string;
  };
  style: Record<string, unknown>;
  hoverStyle?: Record<string, unknown>;
  activeStyle?: Record<string, unknown>;
  visitedStyle?: Record<string, unknown>;
  margin?: { top: number; right: number; bottom: number; left: number };
  padding?: { top: number; right: number; bottom: number; left: number };
  content: Record<string, unknown>;
  visible: boolean;
  parentId?: string;
  zIndex: number;
  pointerEvents?: boolean;
  escToHide?: boolean;
  hideOnStart?: boolean;
  dataBindings?: Record<string, DataBinding>; // Property path -> binding
  cmsAction?: CMSAction; // CMS action for this component
}

const DropdownRenderer = ({
  component,
  baseStyleWithMargin,
  handleClick,
  handleMouseEnter,
  handleMouseLeave,
  handleMoveMouseDown,
}: {
  component: SkinComponent;
  baseStyleWithMargin: React.CSSProperties;
  handleClick: (e: React.MouseEvent) => void;
  handleMouseEnter: (e: React.MouseEvent) => void;
  handleMouseLeave: (e: React.MouseEvent) => void;
  handleMoveMouseDown: (component: SkinComponent, e: React.MouseEvent) => void;
}) => {
  const { project, panoramas, hotspots, setSelectedPanorama, setSelectedHotspot, requestFocusHotspot } =
    useEditorStore();
  const [rows, setRows] = useState<Array<Record<string, unknown>>>([]);
  const [query, setQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  const dataSourceId = typeof component.content?.dataSourceId === "string"
    ? (component.content.dataSourceId as string)
    : "";
  const useManualOptions = Boolean(component.content?.useManualOptions);
  const labelField = typeof component.content?.labelField === "string"
    ? (component.content.labelField as string)
    : "";
  const placeholder =
    typeof component.content?.placeholder === "string"
      ? (component.content.placeholder as string)
      : "Select option";
  const actionType =
    typeof component.content?.actionType === "string"
      ? (component.content.actionType as string)
      : dataSourceId.toLowerCase().includes("hotspot")
        ? "hotspot"
        : dataSourceId.toLowerCase().includes("panorama")
          ? "panorama"
          : "none";

  useEffect(() => {
    let cancelled = false;
    const loadRows = async () => {
      if (!dataSourceId || !project?.craftspace_id) {
        setRows([]);
        return;
      }
      try {
        const datasetWithContent = await CMSService.loadCMSDataWithContent(
          dataSourceId,
          project.craftspace_id,
        );
        if (!cancelled) {
          setRows((datasetWithContent.data?.rows || []) as Array<Record<string, unknown>>);
        }
      } catch (error) {
        console.error("Error loading dropdown dataset:", error);
        if (!cancelled) setRows([]);
      }
    };
    void loadRows();
    return () => {
      cancelled = true;
    };
  }, [dataSourceId, project?.craftspace_id]);

  const manualOptions = Array.isArray(component.content?.options)
    ? (component.content.options as string[])
    : ["Option 1", "Option 2"];
  const options = dataSourceId ? rows : [];

  const selectedIndex =
    typeof component.content?.selectedIndex === "number"
      ? (component.content.selectedIndex as number)
      : 0;

  const getOptionLabel = (row: Record<string, unknown>, index: number) => {
    if (!dataSourceId) {
      if (useManualOptions) {
        return manualOptions[index] || placeholder;
      }
      const panorama = panoramas[index];
      return panorama?.name || placeholder;
    }
    const labelCandidate =
      (labelField && row?.[labelField]) ||
      row?.name ||
      row?.title ||
      row?.id ||
      row?.panorama_name ||
      row?.hotspot_name;
    return labelCandidate !== undefined && labelCandidate !== null
      ? String(labelCandidate)
      : `${placeholder} ${index + 1}`;
  };

  const handleSelectionAction = (row: Record<string, unknown> | undefined) => {
    if (!row) return;
    if (actionType === "panorama") {
      const panoramaId = (row.id || row.panorama_id || row.panoramaId) as string | undefined;
      if (!panoramaId) return;
      const panorama = panoramas.find((p) => p.id === panoramaId);
      if (panorama) {
        setSelectedPanorama(panorama);
      }
      return;
    }
    if (actionType === "hotspot") {
      const hotspotId = (row.id || row.hotspot_id || row.hotspotId) as string | undefined;
      const panoramaId = (row.panorama_id || row.panoramaId) as string | undefined;
      if (panoramaId) {
        const panorama = panoramas.find((p) => p.id === panoramaId);
        if (panorama) {
          setSelectedPanorama(panorama);
        }
      }
      if (hotspotId) {
        const hotspot = hotspots.find((h) => h.id === hotspotId);
        if (hotspot) {
          setSelectedHotspot(hotspot);
          requestFocusHotspot(hotspot.id);
        }
      }
    }
  };

  const optionEntries = options.map((option, index) => {
    return {
      index,
      label: getOptionLabel(options[index] as Record<string, unknown>, index),
      row: rows[index],
    };
  });

  const filteredEntries = optionEntries.filter((entry) =>
    entry.label.toLowerCase().includes(query.trim().toLowerCase()),
  );

  const applySelection = (entry: { index: number; label: string; row?: Record<string, unknown> }) => {
    const updateSkinComponent = useEditorStore.getState().updateSkinComponent;
    updateSkinComponent(component.id, {
      content: {
        ...component.content,
        selectedIndex: entry.index,
      },
    });

    if (!dataSourceId && !useManualOptions) {
      return;
    }
    if (dataSourceId) {
      handleSelectionAction(entry.row);
    }

    setQuery(entry.label);
    setIsOpen(false);
  };

  useEffect(() => {
    const selectedEntry = optionEntries[selectedIndex];
    setQuery(selectedEntry?.label || "");
  }, [selectedIndex, options.length, component.id]);

  return (
    <div
      key={component.id}
      className="component-container"
      data-skin-component
      data-component-id={component.id}
      data-parent-id={component.parentId}
      role="button"
      tabIndex={0}
      onClick={(e) => {
        e.stopPropagation();
        handleClick(e);
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          e.stopPropagation();
          handleClick(e as unknown as React.MouseEvent);
        }
      }}
      onMouseDown={(e) => {
        handleMoveMouseDown(component, e);
      }}
      style={{
        ...baseStyleWithMargin,
        backgroundColor: "transparent",
        border: "none",
        padding: 0,
        overflow: "visible",
      }}
    >
      <div className="relative h-full w-full">
        <input
          value={query}
          onFocus={() => {
            setIsOpen(true);
            setActiveIndex(0);
          }}
          onBlur={() => {
            window.setTimeout(() => setIsOpen(false), 120);
          }}
          onChange={(e) => {
            setQuery(e.target.value);
            setIsOpen(true);
            setActiveIndex(0);
          }}
          onKeyDown={(e) => {
            e.stopPropagation();
            if (!isOpen) return;
            if (e.key === "ArrowDown") {
              e.preventDefault();
              if (filteredEntries.length === 0) return;
              setActiveIndex((prev) => (prev + 1) % filteredEntries.length);
              return;
            }
            if (e.key === "ArrowUp") {
              e.preventDefault();
              if (filteredEntries.length === 0) return;
              setActiveIndex((prev) => (prev - 1 + filteredEntries.length) % filteredEntries.length);
              return;
            }
            if (e.key === "Enter") {
              e.preventDefault();
              const entry = filteredEntries[activeIndex];
              if (entry) applySelection(entry);
              return;
            }
            if (e.key === "Escape") {
              e.preventDefault();
              setIsOpen(false);
            }
          }}
            placeholder={placeholder}
          style={{
            width: "100%",
            height: "100%",
            backgroundColor:
              resolveColorMixValue(component.style.backgroundColor as string | undefined) ??
              "#1f2937",
            color: (component.style.color as string | undefined) ?? "#ffffff",
            borderColor: (component.style.borderColor as string | undefined) ?? "#374151",
            borderWidth: (component.style.borderWidth as number | string | undefined) ?? 1,
            borderStyle: "solid",
            borderRadius: (component.style.borderRadius as number | string | undefined) ?? 6,
            padding: (component.style.padding as string | undefined) ?? "6px 10px",
            fontSize: (component.style.fontSize as string | undefined) ?? "12px",
            fontFamily:
              (component.style.fontFamily as string | undefined) ?? "Arial, sans-serif",
            outline: "none",
          }}
        />
        {isOpen && filteredEntries.length > 0 && (
          <div
            className="absolute left-0 right-0 mt-1 max-h-44 overflow-y-auto rounded border border-gray-600 bg-gray-800 shadow-lg z-[4000]"
            onMouseDown={(e) => e.preventDefault()}
          >
            {filteredEntries.map((entry, idx) => (
              <button
                key={`${component.id}-entry-${entry.index}`}
                type="button"
                className={`w-full px-2 py-1.5 text-left text-xs ${
                  idx === activeIndex
                    ? "bg-gray-700 text-white"
                    : "text-gray-200 hover:bg-gray-700"
                }`}
                onMouseEnter={() => setActiveIndex(idx)}
                onClick={(e) => {
                  e.stopPropagation();
                  applySelection(entry);
                }}
              >
                {entry.label}
              </button>
            ))}
          </div>
        )}
        {isOpen && filteredEntries.length === 0 && (
          <div className="absolute left-0 right-0 mt-1 rounded border border-gray-600 bg-gray-800 px-2 py-1.5 text-xs text-gray-400 z-[4000]">
            No matches
          </div>
        )}
      </div>
    </div>
  );
};

interface ComponentRendererProps {
  components: SkinComponent[];
  selectedComponent: SkinComponent | null;
  onComponentSelect: (component: SkinComponent) => void;
  onUpdateComponents?: (components: SkinComponent[]) => void;
  onPanoramaChange?: (
    panorama: Record<string, unknown>,
    component?: SkinComponent,
    action?: Record<string, unknown>,
  ) => void;
  panoramas?: Array<Record<string, unknown>>;
  isPreviewMode?: boolean;
  allowSelectionInPreview?: boolean;
  currentCMSRowData?: Record<string, unknown> | null;
}

const ComponentRenderer: React.FC<ComponentRendererProps> = ({
  components,
  selectedComponent,
  onComponentSelect,
  onUpdateComponents,
  onPanoramaChange,
  panoramas = [],
  isPreviewMode = false,
  allowSelectionInPreview = false,
  currentCMSRowData: propCurrentCMSRowData,
}) => {
  const { toast } = useToast();
  const {
    project,
    activeCMSDatasetId,
    activeCMSRowIndex,
    setActiveCMSRecord,
    resolvedBindings,
    setResolvedBindings,
    currentCMSRowData,
    setCurrentCMSRowData,
    showSkinComponents,
    hideSkinComponents,
    toggleSkinComponents,
    selectedPanorama,
  } = useEditorStore();
  const [editingTextId, setEditingTextId] = useState<string | null>(null);
  const [editingTextValue, setEditingTextValue] = useState<string>("");
  const lastPointerActionRef = useRef<{ componentId: string; time: number } | null>(null);
  const previewMode = isPreviewMode || useEditorStore.getState().isPreviewMode;
  const selectionEnabled = !previewMode || allowSelectionInPreview;
  const hideOnStartSnapshotRef = useRef<Map<string, boolean>>(new Map());
  const prevPreviewModeRef = useRef(previewMode);
  const previewModeRef = useRef(previewMode);
  previewModeRef.current = previewMode;

  useEffect(() => {
    console.log("ComponentRenderer active:", {
      isPreviewMode: previewMode,
      componentCount: components.length,
    });
  }, [previewMode, components.length]);

  useEffect(() => {
    if (!onUpdateComponents) return;

    const snapshot = hideOnStartSnapshotRef.current;
    const previewModeChanged = prevPreviewModeRef.current !== previewMode;
    prevPreviewModeRef.current = previewMode;

    if (previewMode) {
      if (previewModeChanged) {
        snapshot.clear();
      }

      const newlyHiddenIds: string[] = [];

      components.forEach((comp) => {
        if (comp.hideOnStart === true && !snapshot.has(comp.id)) {
          snapshot.set(comp.id, comp.visible);
          newlyHiddenIds.push(comp.id);
        }
      });

      if (newlyHiddenIds.length > 0) {
        const updatedComponents = components.map((comp) =>
          newlyHiddenIds.includes(comp.id) ? { ...comp, visible: false } : comp,
        );
        onUpdateComponents(updatedComponents);
      }

      return;
    }

    if (snapshot.size === 0) return;

    const needsRestore = components.some(
      (comp) => snapshot.has(comp.id) && comp.visible !== snapshot.get(comp.id),
    );

    if (needsRestore) {
      const updatedComponents = components.map((comp) =>
        snapshot.has(comp.id) ? { ...comp, visible: snapshot.get(comp.id)! } : comp,
      );
      onUpdateComponents(updatedComponents);
    }

    snapshot.clear();
  }, [previewMode, components, onUpdateComponents]);

  // Handle keyboard shortcuts for duplication and Esc key to hide
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement ||
        e.target instanceof HTMLSelectElement
      ) {
        return;
      }

      // Handle Esc key to hide components (only in preview mode)
      if (isPreviewMode && e.key === "Escape") {
        e.preventDefault();
        // Find components that have escToHide enabled and hide them
        const componentsToHide = components.filter((comp) => comp.escToHide === true);
        if (componentsToHide.length > 0 && onUpdateComponents) {
          const updatedComponents = components.map((comp) =>
            comp.escToHide === true ? { ...comp, visible: false } : comp,
          );
          onUpdateComponents(updatedComponents);
        }
        return;
      }

      // Only handle other shortcuts when not in preview mode and a component is selected
      if (isPreviewMode || !selectedComponent) return;

      // Cmd+C / Ctrl+C to copy component
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "c") {
        e.preventDefault();
        const copyEvent = new CustomEvent("copySkinComponent", {
          detail: { component: selectedComponent },
        });
        document.dispatchEvent(copyEvent);
        return;
      }

      // Cmd+V / Ctrl+V to paste component
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "v") {
        e.preventDefault();
        const pasteEvent = new CustomEvent("pasteSkinComponent", {
          detail: { target: selectedComponent },
        });
        document.dispatchEvent(pasteEvent);
        return;
      }

      // Cmd+D (Mac) or Ctrl+D (Windows/Linux) to duplicate
      if ((e.metaKey || e.ctrlKey) && e.key === "d") {
        e.preventDefault();
        // We need to call the parent's duplicate function
        // For now, we'll use a custom event that the parent can listen to
        const duplicateEvent = new CustomEvent("duplicateComponent", {
          detail: { component: selectedComponent },
        });
        document.dispatchEvent(duplicateEvent);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [selectedComponent, isPreviewMode, components, onUpdateComponents]);

  // Use prop data if available, otherwise disable CMS data loading for now
  useEffect(() => {
    if (propCurrentCMSRowData) {
      setCurrentCMSRowData(propCurrentCMSRowData);
    } else {
      // TODO: Fix CMS data loading - loadCMSDataWithContent expects record ID, not dataset ID
      console.log("⚠️ CMS data loading disabled - need to fix dataset vs record ID issue");
      setCurrentCMSRowData(null);
    }
  }, [propCurrentCMSRowData, setCurrentCMSRowData]);

  // Resolve data bindings when components change
  useEffect(() => {
    const resolveBindings = async () => {
      if (!project?.craftspace_id) return;

      const newResolvedBindings: Record<string, Record<string, string>> = {};

      for (const component of components) {
        if (component.dataBindings) {
          newResolvedBindings[component.id] = {};

          for (const [propertyPath, binding] of Object.entries(component.dataBindings)) {
            try {
              const resolvedValue = await DataBindingService.resolveBinding(
                binding,
                project.craftspace_id,
                activeCMSRowIndex, // Use active row index from global state
              );
              newResolvedBindings[component.id][propertyPath] = resolvedValue;
            } catch (error) {
              console.error("Error resolving binding:", error);
              newResolvedBindings[component.id][propertyPath] = "";
            }
          }
        }
      }

      setResolvedBindings(newResolvedBindings);
    };

    resolveBindings();
  }, [components, project?.craftspace_id, activeCMSRowIndex, setResolvedBindings]);

  // Helper function to render content with CMS data
  const resolveSkinTextTemplate = (value: string, _component: SkinComponent): string => {
    const currentPanoramaName =
      (selectedPanorama as { name?: string } | null | undefined)?.name || "";

    return value.replace(/\[\s*(hotspots|panoramas)\.(\w+)\s*\]/g, (_match, table, field) => {
      if (table === "panoramas" && field === "name") return currentPanoramaName;
      return "";
    });
  };

  const renderContent = (component: SkinComponent, field: string): React.ReactNode => {
    const propertyPath = `content.${field}`;
    const binding = component.dataBindings?.[propertyPath];

    if (binding && resolvedBindings[component.id]?.[propertyPath]) {
      return resolvedBindings[component.id][propertyPath];
    }

    const content = component.content?.[field] ?? "";

    // For text field in text components only, show "Text" placeholder when content is empty
    if (field === "text" && component.type === "text") {
      const rawText = typeof content === "string" ? content : "";
      if (!rawText || rawText.trim() === "") {
        return "Text";
      }
      return resolveSkinTextTemplate(rawText, component);
    }

    return content;
  };

  // Helper function to render property values with CMS data (for non-content properties)
  const renderProperty = (component: SkinComponent, property: string): React.ReactNode => {
    const binding = component.dataBindings?.[property];

    if (binding && resolvedBindings[component.id]?.[property]) {
      const resolvedValue = resolvedBindings[component.id][property];

      // Special handling for visibility property - convert string booleans to actual booleans
      if (property === "visible") {
        if (typeof resolvedValue === "string") {
          // Convert string representations to boolean
          const lowerValue = resolvedValue.toLowerCase().trim();
          if (lowerValue === "true" || lowerValue === "1" || lowerValue === "yes") {
            return true;
          } else if (
            lowerValue === "false" ||
            lowerValue === "0" ||
            lowerValue === "no" ||
            lowerValue === ""
          ) {
            return false;
          }
        }
        // If it's already a boolean, return as-is
        return Boolean(resolvedValue);
      }

      return resolvedValue;
    }

    return component[property as keyof SkinComponent];
  };

  // Helper function to get CMS data as string for attributes
  const _getCMSDataAsString = (component: SkinComponent, field: string): string => {
    const propertyPath = `content.${field}`;
    const binding = component.dataBindings?.[propertyPath];

    if (binding && resolvedBindings[component.id]?.[propertyPath]) {
      return resolvedBindings[component.id][propertyPath];
    }

    const value = component.content?.[field];
    return value !== undefined && value !== null ? String(value) : "";
  };

  // Handle component click for CMS actions
  const handleComponentClick = (component: SkinComponent, event: React.MouseEvent) => {
    // Only handle CMS actions in preview mode or if explicitly enabled
    if (component.cmsAction && (isPreviewMode || true)) {
      event.preventDefault();
      event.stopPropagation();

      CMSActionService.executeCMSAction(component.cmsAction, setActiveCMSRecord);
      return;
    }

    // Default selection behavior when not in preview mode
    if (selectionEnabled) {
      onComponentSelect(component);
    }
  };

  // Helper functions to convert margin/padding objects to CSS strings
  const _spaceToCSS = (
    space: { top: number; right: number; bottom: number; left: number } | undefined,
  ): string => {
    if (!space) return "";
    if (space.top === space.right && space.right === space.bottom && space.bottom === space.left) {
      return `${space.top}px`;
    }
    return `${space.top}px ${space.right}px ${space.bottom}px ${space.left}px`;
  };

  // Evaluate conditional visibility rules synchronously
  const evaluateConditionalVisibilitySync = (
    _component: SkinComponent,
    rules: Array<Record<string, unknown>>,
  ) => {
    if (!project?.craftspace_id || !activeCMSDatasetId || !currentCMSRowData) {
      return false; // Show component if no data available
    }

    try {
      // Evaluate each rule against the current CMS row data
      for (const rule of rules) {
        const fieldValue = currentCMSRowData[rule.fieldName];
        const ruleResult = evaluateRule(rule, fieldValue);

        if (!ruleResult) {
          return true; // Hide component if any rule fails
        }
      }

      return false; // Show component if all rules pass
    } catch (error) {
      console.error("Error evaluating conditional visibility:", error);
      return false; // Show component on error
    }
  };

  // Evaluate a single rule
  const evaluateRule = (rule: Record<string, unknown>, fieldValue: unknown) => {
    // Convert field value to string for consistent comparison
    const stringValue = fieldValue !== null && fieldValue !== undefined ? String(fieldValue) : "";

    let result = false;

    switch (rule.operator) {
      case "is_on":
        result = stringValue === "true" || fieldValue === true;
        break;
      case "is_off":
        result = stringValue === "false" || fieldValue === false || !fieldValue;
        break;
      case "is_set":
        result = fieldValue !== null && fieldValue !== undefined && stringValue !== "";
        break;
      case "is_not_set":
        result = fieldValue === null || fieldValue === undefined || stringValue === "";
        break;
      case "equals":
        result = stringValue === rule.value;
        break;
      case "not_equals":
        result = stringValue !== rule.value;
        break;
      case "contains":
        result = stringValue?.includes(rule.value);
        break;
      case "not_contains":
        result = !stringValue || !stringValue.includes(rule.value);
        break;
      case "greater_than":
        result = parseFloat(stringValue) > parseFloat(rule.value);
        break;
      case "less_than":
        result = parseFloat(stringValue) < parseFloat(rule.value);
        break;
      case "is_after":
        result = new Date(stringValue) >= new Date(rule.value);
        break;
      case "is_before":
        result = new Date(stringValue) <= new Date(rule.value);
        break;
      default:
        result = true; // Show component for unknown operators
    }

    return result;
  };

  // Inline text editing functions
  const startTextEditing = (component: SkinComponent) => {
    if (component.type !== "text" || isPreviewMode) return;

    const currentText = (component.content?.text as string) || "";

    setEditingTextId(component.id);
    setEditingTextValue(currentText === "Text" ? "" : currentText);
  };

  const saveTextEditing = (component: SkinComponent) => {
    if (!editingTextId || editingTextId !== component.id) return;

    const updateSkinComponent = useEditorStore.getState().updateSkinComponent;
    updateSkinComponent(component.id, {
      content: { ...component.content, text: editingTextValue },
    });

    setEditingTextId(null);
    setEditingTextValue("");
  };

  const cancelTextEditing = () => {
    setEditingTextId(null);
    setEditingTextValue("");
  };

  const handleTextKeyDown = (e: React.KeyboardEvent, component: SkinComponent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      saveTextEditing(component);
    } else if (e.key === "Escape") {
      e.preventDefault();
      cancelTextEditing();
    }
  };

  const dragStateRef = React.useRef<{
    componentId: string;
    startX: number;
    startY: number;
    lastX: number;
    lastY: number;
    startLeft: number;
    startTop: number;
    parentRect: DOMRect;
    parentBorders: { left: number; top: number; right: number; bottom: number };
    leftMode: "percent" | "px";
    topMode: "percent" | "px";
  } | null>(null);

  const getPositionMode = (value: unknown): "percent" | "px" => {
    if (typeof value === "string" && value.trim().endsWith("%")) return "percent";
    return "px";
  };

  const getParentInnerSize = (
    rect: DOMRect,
    borders: { left: number; top: number; right: number; bottom: number },
  ) => ({
    width: Math.max(1, rect.width - borders.left - borders.right),
    height: Math.max(1, rect.height - borders.top - borders.bottom),
  });

  const getComponentById = (id: string) => components.find((c) => c.id === id);

  const isDescendant = (childId: string, ancestorId: string) => {
    let current = getComponentById(childId);
    while (current?.parentId) {
      if (current.parentId === ancestorId) return true;
      current = getComponentById(current.parentId);
    }
    return false;
  };

  const getContainerTargetAtPoint = (x: number, y: number, draggedId: string) => {
    let bestTarget: {
      targetId: string;
      targetEl: HTMLElement;
      targetComponent: SkinComponent;
      area: number;
    } | null = null;

    for (const candidate of components) {
      if (candidate.id === draggedId) continue;
      if (candidate.type !== "box" && candidate.type !== "collection-list") continue;
      if (isDescendant(candidate.id, draggedId)) continue;

      const targetEl = document.querySelector<HTMLElement>(
        `[data-component-id='${candidate.id}']`,
      );
      if (!targetEl) continue;
      const rect = targetEl.getBoundingClientRect();
      if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) continue;

      const area = rect.width * rect.height;
      if (!bestTarget || area < bestTarget.area) {
        bestTarget = {
          targetId: candidate.id,
          targetEl,
          targetComponent: candidate,
          area,
        };
      }
    }

    return bestTarget
      ? {
          targetId: bestTarget.targetId,
          targetEl: bestTarget.targetEl,
          targetComponent: bestTarget.targetComponent,
        }
      : null;
  };

  const handleMoveMouseDown = (component: SkinComponent, e: React.MouseEvent) => {
    // Dragging skin components is intentionally disabled.
    return;
    if (isPreviewMode) return;
    if (e.button !== 0) return;
    const target = e.target as HTMLElement;
    if (target.closest(".resize-handle")) return;
    if (target.closest("textarea, input, [contenteditable='true']")) return;

    const element = e.currentTarget as HTMLElement;
    const offsetParent = element.offsetParent as HTMLElement | null;
    if (!offsetParent) return;

    e.stopPropagation();
    e.preventDefault();

    const parentRect = offsetParent.getBoundingClientRect();
    const parentStyle = window.getComputedStyle(offsetParent);
    const parentBorders = {
      left: parseFloat(parentStyle.borderLeftWidth || "0") || 0,
      top: parseFloat(parentStyle.borderTopWidth || "0") || 0,
      right: parseFloat(parentStyle.borderRightWidth || "0") || 0,
      bottom: parseFloat(parentStyle.borderBottomWidth || "0") || 0,
    };

    dragStateRef.current = {
      componentId: component.id,
      startX: e.clientX,
      startY: e.clientY,
      lastX: e.clientX,
      lastY: e.clientY,
      startLeft: element.offsetLeft,
      startTop: element.offsetTop,
      parentRect,
      parentBorders,
      leftMode: getPositionMode(component.position.left),
      topMode: getPositionMode(component.position.top),
    };

    if (selectionEnabled && onComponentSelect) {
      onComponentSelect(component);
    }

    const previousPointerEvents = element.style.pointerEvents;
    element.style.pointerEvents = "none";

    const handleMouseMove = (event: MouseEvent) => {
      const dragState = dragStateRef.current;
      if (!dragState) return;
      dragState.lastX = event.clientX;
      dragState.lastY = event.clientY;
      const dx = event.clientX - dragState.startX;
      const dy = event.clientY - dragState.startY;

      const nextLeftPx = dragState.startLeft + dx;
      const nextTopPx = dragState.startTop + dy;

      const { width: parentWidth, height: parentHeight } = getParentInnerSize(
        dragState.parentRect,
        dragState.parentBorders,
      );
      const elementWidth = element.offsetWidth;
      const elementHeight = element.offsetHeight;

      // Clamp movement so the component stays within its parent canvas
      const maxLeft = Math.max(0, parentWidth - elementWidth);
      const maxTop = Math.max(0, parentHeight - elementHeight);
      const clampedLeftPx = Math.min(Math.max(0, nextLeftPx), maxLeft);
      const clampedTopPx = Math.min(Math.max(0, nextTopPx), maxTop);

      const nextLeft =
        dragState.leftMode === "percent"
          ? `${(clampedLeftPx / parentWidth) * 100}%`
          : Math.round(clampedLeftPx);
      const nextTop =
        dragState.topMode === "percent"
          ? `${(clampedTopPx / parentHeight) * 100}%`
          : Math.round(clampedTopPx);

      const updateSkinComponent = useEditorStore.getState().updateSkinComponent;
      updateSkinComponent(component.id, {
        position: {
          ...component.position,
          left: nextLeft,
          top: nextTop,
          right: undefined,
          bottom: undefined,
        },
      });
    };

    const finishDrag = (pointerX: number, pointerY: number) => {
      const dragState = dragStateRef.current;
      dragStateRef.current = null;
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      window.removeEventListener("mouseup", handleMouseUp);
      window.removeEventListener("blur", handleWindowBlur);
      element.style.pointerEvents = previousPointerEvents;
      if (!dragState) return;

      const dropTarget = getContainerTargetAtPoint(pointerX, pointerY, component.id);
      const nextParentId = dropTarget?.targetId ?? null;
      const targetEl =
        dropTarget?.targetEl ??
        (document.querySelector("[data-skin-root]") as HTMLElement | null);

      if (!targetEl) return;

      if (nextParentId !== component.parentId) {
        const elementRect = element.getBoundingClientRect();
        const targetRect = targetEl.getBoundingClientRect();
        const targetStyle = window.getComputedStyle(targetEl);
        const targetBorders = {
          left: parseFloat(targetStyle.borderLeftWidth || "0") || 0,
          top: parseFloat(targetStyle.borderTopWidth || "0") || 0,
          right: parseFloat(targetStyle.borderRightWidth || "0") || 0,
          bottom: parseFloat(targetStyle.borderBottomWidth || "0") || 0,
        };
        const { width: targetWidth, height: targetHeight } = getParentInnerSize(
          targetRect,
          targetBorders,
        );

        const parentPadding = getParentPaddingForComponent(nextParentId ?? undefined, components);
        const relativeLeftPx =
          elementRect.left - targetRect.left - targetBorders.left - parentPadding.left;
        const relativeTopPx =
          elementRect.top - targetRect.top - targetBorders.top - parentPadding.top;

        const nextLeft =
          dragState.leftMode === "percent"
            ? `${(relativeLeftPx / targetWidth) * 100}%`
            : Math.round(relativeLeftPx);
        const nextTop =
          dragState.topMode === "percent"
            ? `${(relativeTopPx / targetHeight) * 100}%`
            : Math.round(relativeTopPx);

        const updateSkinComponent = useEditorStore.getState().updateSkinComponent;
        const nestSkinComponent = useEditorStore.getState().nestSkinComponent;
        updateSkinComponent(component.id, {
          position: {
            ...component.position,
            left: nextLeft,
            top: nextTop,
            right: undefined,
            bottom: undefined,
          },
        });
        nestSkinComponent(component.id, nextParentId);
      }
    };

    const handleMouseUp = (event: MouseEvent) => {
      const dragState = dragStateRef.current;
      const pointerX = Number.isFinite(event.clientX) ? event.clientX : dragState?.lastX ?? 0;
      const pointerY = Number.isFinite(event.clientY) ? event.clientY : dragState?.lastY ?? 0;
      finishDrag(pointerX, pointerY);
    };

    const handleWindowBlur = () => {
      const dragState = dragStateRef.current;
      if (!dragState) return;
      finishDrag(dragState.lastX, dragState.lastY);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    window.addEventListener("mouseup", handleMouseUp);
    window.addEventListener("blur", handleWindowBlur);
  };

  const componentHasActions = (component: SkinComponent) =>
    !!(
      (Array.isArray(component.content?.actions) && component.content.actions.length > 0) ||
      component.content?.linkedPanoramaId ||
      component.content?.notificationMessage ||
      component.content?.notificationTitle ||
      component.content?.downloadFileUrl ||
      (component.content?.skinVisibilityTargetComponentIds &&
        component.content.skinVisibilityTargetComponentIds.length > 0) ||
      (component.content?.hotspotVisibilityTargetIds &&
        component.content.hotspotVisibilityTargetIds.length > 0) ||
      component.cmsAction
    );

  const getNavigateTargetPanoramaIds = (component: SkinComponent): string[] => {
    const targets = new Set<string>();
    const actions = Array.isArray(component.content?.actions)
      ? (component.content?.actions as Array<Record<string, unknown>>)
      : [];
    for (const action of actions) {
      if ((action?.type as string) === "navigate" && action.linkedPanoramaId) {
        targets.add(String(action.linkedPanoramaId));
      }
    }
    if (component.content?.linkedPanoramaId) {
      targets.add(String(component.content.linkedPanoramaId));
    }
    return Array.from(targets);
  };

  const isComponentActive = (component: SkinComponent): boolean => {
    const currentPanoramaId = selectedPanorama?.id;
    if (!currentPanoramaId) return false;
    return getNavigateTargetPanoramaIds(component).some((targetId) => targetId === currentPanoramaId);
  };

  const isComponentVisited = (component: SkinComponent): boolean => {
    if (!panoramas) return false;
    const visitedIds = new Set(
      panoramas.filter((panorama) => (panorama as { isDiscovered?: boolean }).isDiscovered).map((p) => p.id),
    );
    if (visitedIds.size === 0) return false;
    return getNavigateTargetPanoramaIds(component).some((targetId) => visitedIds.has(targetId));
  };

  const getSkinStylePresetStyle = (component: SkinComponent): Record<string, unknown> | null => {
    const presetId = component.content?.skinStylePresetId as string | undefined;
    if (!presetId) return null;
    const presets = (project?.data as Record<string, unknown> | null | undefined)?.skinStylePresets;
    if (!Array.isArray(presets)) return null;
    const match = presets.find(
      (preset) =>
        preset &&
        typeof preset.id === "string" &&
        preset.id === presetId &&
        preset.componentType === component.type &&
        typeof preset.style === "object",
    ) as { style?: Record<string, unknown> } | undefined;
    return match?.style || null;
  };

  const triggerComponentActions = (
    component: SkinComponent,
    options: { isModifierPressed: boolean; trigger: "click" | "mouseenter" | "mouseleave" },
  ) => {
    const shouldTriggerActions = previewModeRef.current || options.isModifierPressed;
    if (!shouldTriggerActions) return false;

    // Read actions array, with backward compat for old flat structure
    const actions = (component.content?.actions as Array<Record<string, unknown>>) || [];

    if (actions.length > 0) {
      // New multi-action format
      let actionsTriggered = 0;
      actions.forEach((action) => {
        const actionTrigger = (action.trigger as string) || "click";
        if (actionTrigger !== options.trigger) return;

        const type = action.type as string;

        if (type === "navigate" && action.linkedPanoramaId) {
          const linkedPanorama = panoramas?.find((p) => p.id === action.linkedPanoramaId);
          if (linkedPanorama) {
            const linkTransitionType = action.linkTransitionType;
            const normalizedTransitionType = linkTransitionType || "default";
            console.log("🎨 Component transition type:", {
              componentId: component.id,
              linkTransitionType,
              normalizedTransitionType,
            });
            if (onPanoramaChange) onPanoramaChange(linkedPanorama, component, action);
          } else {
            toast({
              variant: "destructive",
              title: "Error",
              description: "Linked panorama not found.",
            });
          }
        } else if (type === "notification") {
          const title = (action.notificationTitle as string) || "Notification";
          const message = (action.notificationMessage as string) || "";
          const autoHideDuration = action.notificationAutoHideDuration as number | undefined;
          toast({
            title,
            description: message,
            duration: autoHideDuration !== undefined ? autoHideDuration * 1000 : 1000000000,
            variant: autoHideDuration === undefined ? "alert" : "default",
          });
        } else if (type === "download" && action.downloadFileUrl) {
          const link = document.createElement("a");
          link.href = action.downloadFileUrl as string;
          link.download = (action.downloadFileName as string) || "download";
          link.target = "_blank";
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        } else if (type === "showHideSkin") {
          const targetIds = (action.skinVisibilityTargetComponentIds as string[]) || [];
          const visAction = action.skinVisibilityAction as string;
          if (visAction && targetIds.length > 0) {
            console.log("Triggering skin visibility action:", {
              componentId: component.id,
              action: visAction,
              targetIds,
            });
            if (visAction === "show") showSkinComponents(targetIds);
            else if (visAction === "hide") hideSkinComponents(targetIds);
            else if (visAction === "toggle") toggleSkinComponents(targetIds);
          }
        } else if (type === "showHideHotspots") {
          let targetIds = (action.hotspotVisibilityTargetIds as string[]) || [];
          const config = action.hotspotVisibilityItemsConfig as Record<string, unknown>;
          if (config?.mode === "dynamic" && targetIds.length === 0) {
            import("@/store/editorStore").then(({ useEditorStore }) => {
              const store = useEditorStore.getState();
              targetIds = store.hotspots.map((h) => h.id);
            });
          }
          const visAction = action.hotspotVisibilityAction as string;
          if (visAction && targetIds.length > 0) {
            import("@/store/editorStore").then(({ useEditorStore }) => {
              const store = useEditorStore.getState();
              if (visAction === "show") store.showHotspots(targetIds);
              else if (visAction === "hide") store.hideHotspots(targetIds);
              else if (visAction === "toggle") store.toggleHotspots(targetIds);
            });
          }
        }
        actionsTriggered++;
      });
      return actionsTriggered > 0;
    } else {
      // Backward compat: old flat structure
      if (options.trigger !== "click") return false;
      let actionsTriggered = 0;

      // Handle panorama linking if component has one
      const linkedPanoramaId = component.content?.linkedPanoramaId;
      if (linkedPanoramaId && onPanoramaChange && panoramas) {
        const linkedPanorama = panoramas.find((p) => p.id === linkedPanoramaId);
        if (linkedPanorama) {
          const linkTransitionType = component.content?.linkTransitionType;
          const normalizedTransitionType = linkTransitionType || "default";
          console.log("🎨 Component transition type:", {
            componentId: component.id,
            linkTransitionType,
            normalizedTransitionType,
          });

          onPanoramaChange(linkedPanorama, component);
          actionsTriggered++;
        } else {
          toast({
            variant: "destructive",
            title: "Error",
            description: "Linked panorama not found.",
          });
        }
      }

      // Show notification if component has one
      const notificationMessage = component.content?.notificationMessage;
      const notificationTitle = component.content?.notificationTitle;
      if (notificationMessage || notificationTitle) {
        const title = notificationTitle || "Notification";
        const message = notificationMessage || "";
        const autoHideDuration = component.content?.notificationAutoHideDuration;
        const isAlert = autoHideDuration === undefined;

        toast({
          title: title,
          description: message,
          duration: autoHideDuration !== undefined ? autoHideDuration * 1000 : 1000000000,
          variant: isAlert ? "alert" : "default",
        });
        actionsTriggered++;
      }

      // Trigger download if component has one
      const downloadFileUrl = component.content?.downloadFileUrl;
      const downloadFileName = component.content?.downloadFileName;
      if (downloadFileUrl) {
        const link = document.createElement("a");
        link.href = downloadFileUrl;
        link.download = downloadFileName || "download";
        link.target = "_blank";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        actionsTriggered++;
      }

      // Handle skin visibility action
      const skinVisibilityTargetComponentIds = component.content?.skinVisibilityTargetComponentIds;
      const skinVisibilityAction = component.content?.skinVisibilityAction;
      if (
        skinVisibilityTargetComponentIds &&
        skinVisibilityTargetComponentIds.length > 0 &&
        skinVisibilityAction
      ) {
        console.log("Triggering skin visibility action:", {
          componentId: component.id,
          action: skinVisibilityAction,
          targetIds: skinVisibilityTargetComponentIds,
        });
        if (skinVisibilityAction === "show") {
          showSkinComponents(skinVisibilityTargetComponentIds);
        } else if (skinVisibilityAction === "hide") {
          hideSkinComponents(skinVisibilityTargetComponentIds);
        } else if (skinVisibilityAction === "toggle") {
          toggleSkinComponents(skinVisibilityTargetComponentIds);
        }
        actionsTriggered++;
      }

      // Handle hotspot visibility action
      const hotspotVisibilityTargetIds = component.content?.hotspotVisibilityTargetIds;
      const hotspotVisibilityAction = component.content?.hotspotVisibilityAction;
      const hotspotVisibilityConfig = component.content?.hotspotVisibilityItemsConfig;

      if (hotspotVisibilityAction) {
        // Determine which hotspots to apply the action to
        let targetIds = hotspotVisibilityTargetIds || [];

        // In dynamic mode with filters/sort, apply action to all hotspots
        // (In a future update, we'd apply filters, but for now we use all available)
        if (hotspotVisibilityConfig?.mode === "dynamic" && targetIds.length === 0) {
          import("@/store/editorStore").then(({ useEditorStore }) => {
            const store = useEditorStore.getState();
            targetIds = store.hotspots.map((h) => h.id);
          });
        }

        if (targetIds.length > 0) {
          import("@/store/editorStore").then(({ useEditorStore }) => {
            const store = useEditorStore.getState();
            if (hotspotVisibilityAction === "show") {
              store.showHotspots(targetIds);
            } else if (hotspotVisibilityAction === "hide") {
              store.hideHotspots(targetIds);
            } else if (hotspotVisibilityAction === "toggle") {
              store.toggleHotspots(targetIds);
            }
          });
          actionsTriggered++;
        }
      }

      return actionsTriggered > 0;
    }
  };

  const renderComponent = (rawComponent: SkinComponent, options?: { flexChild?: boolean }) => {
    let component = rawComponent;
    if (!component.style || typeof component.style !== "object") {
      component = { ...component, style: {} };
    }
    const isVisible = renderProperty(component, "visible") as boolean;
    if (!isVisible) return null;

    // Check conditional visibility rules
    const conditionalVisibilityRules = component.content?.conditionalVisibilityRules;

    if (conditionalVisibilityRules && conditionalVisibilityRules.length > 0) {
      const shouldHide = evaluateConditionalVisibilitySync(component, conditionalVisibilityRules);
      if (shouldHide) return null;
    }

    // Check if component has any actions that require interaction
    const hasActions = componentHasActions(component);
    const presetStyle = getSkinStylePresetStyle(component);
    if (presetStyle) {
      component = { ...component, style: { ...presetStyle, ...component.style } };
    }
    const isActive = isComponentActive(component);
    const isVisited = !isActive && isComponentVisited(component);
    if (previewMode && isActive && component.activeStyle) {
      component = {
        ...component,
        style: { ...component.style, ...(component.activeStyle as Record<string, unknown>) },
      };
    } else if (previewMode && isVisited && component.visitedStyle) {
      component = {
        ...component,
        style: { ...component.style, ...(component.visitedStyle as Record<string, unknown>) },
      };
    }

    // Resolve layout style from a single deterministic function used by both renderer and inspector
    const resolvedLayoutStyle = resolveComponentLayoutStyle(
      component,
      getParentPaddingForComponent(component.parentId, components),
    );

    const baseStyle: React.CSSProperties = options?.flexChild
      ? {
          // Flex children use relative positioning — left/top are handled by the flex container
          position: "relative",
          pointerEvents: "auto",
          width: resolvedLayoutStyle.width,
          height: resolvedLayoutStyle.height,
          minWidth: resolvedLayoutStyle.minWidth,
          minHeight: resolvedLayoutStyle.minHeight,
          maxWidth: resolvedLayoutStyle.maxWidth,
          maxHeight: resolvedLayoutStyle.maxHeight,
          zIndex:
            (component.zIndex ?? 0) +
            (selectedComponent?.id === component.id && !isPreviewMode ? 2000 : 0),
          cursor: hasActions ? "pointer" : "default",
          outline:
            selectedComponent?.id === component.id && !isPreviewMode
              ? "2px solid #0D6CFF"
              : "none",
          opacity: isPreviewMode && !hasActions && component.type === "text" ? 0.8 : 1,
          ...component.style,
        }
      : {
          position: "absolute",
          // Always allow pointer events for skin components - they should be clickable for selection
          pointerEvents: "auto",
          ...resolvedLayoutStyle,
          zIndex:
            (component.zIndex ?? 0) +
            (selectedComponent?.id === component.id && !isPreviewMode ? 2000 : 0),
          cursor: hasActions ? "pointer" : "default",
          outline:
            selectedComponent?.id === component.id && !isPreviewMode
              ? "2px solid #0D6CFF"
              : "none",
          // Add subtle visual indicator for non-interactive components in preview mode
          // Only apply reduced opacity to text components without actions, not images
          opacity: isPreviewMode && !hasActions && component.type === "text" ? 0.8 : 1,
          ...component.style,
        };
    const marginTransform = null;
    const baseStyleWithMargin: React.CSSProperties = {
      ...baseStyle,
      transform: mergeTransforms(baseStyle.transform, marginTransform),
      margin: component.margin
        ? `${component.margin.top}px ${component.margin.right}px ${component.margin.bottom}px ${component.margin.left}px`
        : undefined,
    };

    const handleClick = (e: React.MouseEvent) => {
      e.stopPropagation(); // Prevent event bubbling to avoid closing modals

      console.log("Skin component click:", {
        componentId: component.id,
        componentType: component.type,
        isPreviewMode: previewMode,
        hasActions,
      });

      // Always select the component when clicked

      if (selectionEnabled && onComponentSelect) {
        try {
          onComponentSelect(rawComponent);
        } catch (error) {
          console.error("Error selecting component:", error);
        }
      } else if (selectionEnabled) {
        console.error("onComponentSelect is not defined!");
      }

      // Handle CMS actions if component has them
      if (component.cmsAction) {
        handleComponentClick(component, e);
        return;
      }

      // In preview mode, always allow component selection for editing
      // But also handle actions if the component has them
      if (hasActions && isPreviewMode) {
        // Handle the action in preview mode, but don't return early
        // so component selection still happens
      }

      // In preview mode, actions trigger on simple clicks. In editor mode, they require modifier key
      const isModifierPressed = e.metaKey || e.ctrlKey;

      if (component.content?.skinVisibilityTargetComponentIds?.length) {
        console.log("Skin action click:", {
          componentId: component.id,
          componentType: component.type,
          isPreviewMode: previewMode,
          isModifierPressed,
          targetIds: component.content?.skinVisibilityTargetComponentIds,
          action: component.content?.skinVisibilityAction,
        });
      }

      const recentPointerAction = lastPointerActionRef.current;
      const isDuplicateAction =
        recentPointerAction &&
        recentPointerAction.componentId === component.id &&
        Date.now() - recentPointerAction.time < 300;

      if (!isDuplicateAction) {
        const triggered = triggerComponentActions(component, { isModifierPressed, trigger: "click" });
        if (!triggered && !previewMode && isModifierPressed) {
          toast({
            title: "No actions",
            description: "This component has no actions configured.",
          });
        }
      }
    };

    const handleMouseEnter = (e: React.MouseEvent) => {
      if (!hasActions) return;
      const targetEl = e.target as HTMLElement;
      const targetComponentEl = targetEl.closest("[data-component-id]");
      const targetId = targetComponentEl?.getAttribute("data-component-id");
      if (targetId && targetId !== component.id) {
        const targetComp = components.find((c) => c.id === targetId);
        if (targetComp && componentHasActions(targetComp)) return;
      }
      const isModifierPressed = e.metaKey || e.ctrlKey;
      triggerComponentActions(component, { isModifierPressed, trigger: "mouseenter" });
    };

    const handleMouseLeave = (e: React.MouseEvent) => {
      if (!hasActions) return;
      const isModifierPressed = e.metaKey || e.ctrlKey;
      triggerComponentActions(component, { isModifierPressed, trigger: "mouseleave" });
    };

    const safeStyle = component.style || {};

    switch (component.type) {
      case "text": {
        const startResizeDrag =
          (direction: "n" | "s" | "e" | "w" | "ne" | "nw" | "se" | "sw") =>
          (e: React.MouseEvent) => {
            e.stopPropagation();
            e.preventDefault();
            // Capture the resize handle element that initiated the drag
            const resizeHandle = e.currentTarget as HTMLElement;
            const element = e.currentTarget.parentElement;
            if (!element) return;
            // Find the container that has the proper dimensions for calculations
            let parent = element.parentElement;
            while (parent && parent !== document.body) {
              const rect = parent.getBoundingClientRect();
              // Look for a container that has meaningful dimensions (not just 0x0)
              if (rect.width > 0 && rect.height > 0) {
                break;
              }
              parent = parent.parentElement;
            }
            if (!parent) return;

            const startX = e.clientX;
            const startY = e.clientY;
            const startLeft = element.offsetLeft;
            const startTop = element.offsetTop;
            const startWidth = element.offsetWidth;
            const startHeight = element.offsetHeight;
            const startRight = component.position.right;
            const startBottom = component.position.bottom;
            const parentRect = parent.getBoundingClientRect();
            const minSize = 8;

            const onMouseMove = (ev: MouseEvent) => {
              const dx = ev.clientX - startX;
              const dy = ev.clientY - startY;

              const startRightEdge = startLeft + startWidth;
              const startBottomEdge = startTop + startHeight;

              let nextLeft = startLeft;
              let nextTop = startTop;
              let nextWidth = startWidth;
              let nextHeight = startHeight;

              if (direction.includes("e")) {
                nextWidth = Math.max(minSize, startWidth + dx);
              }
              if (direction.includes("w")) {
                const proposedLeft = startLeft + dx;
                const maxLeft = startRightEdge - minSize;
                const boundedLeft = Math.max(0, Math.min(proposedLeft, maxLeft));
                nextLeft = boundedLeft;
                nextWidth = startRightEdge - boundedLeft;
              }
              if (direction.includes("s")) {
                const maxHeight = parentRect.height - startTop;
                nextHeight = Math.max(minSize, Math.min(startHeight + dy, maxHeight));
              }
              if (direction.includes("n")) {
                const proposedTop = startTop + dy;
                const maxTop = startBottomEdge - minSize;
                const boundedTop = Math.max(0, Math.min(proposedTop, maxTop));
                nextTop = boundedTop;
                nextHeight = startBottomEdge - boundedTop;
              }

              const _nextRight = parentRect.width - (nextLeft + nextWidth);
              const _nextBottom = parentRect.height - (nextTop + nextHeight);

              try {
                const updateSkinComponent = useEditorStore.getState().updateSkinComponent;
                const originalWidth = component.size.width;
                const originalHeight = component.size.height;
                const isWidthPercentage =
                  typeof originalWidth === "string" &&
                  (originalWidth.endsWith("%") || originalWidth === "auto");
                const isHeightPercentage =
                  typeof originalHeight === "string" &&
                  (originalHeight.endsWith("%") || originalHeight === "auto");

                // Check which dimensions are being changed based on direction
                const isChangingWidth = direction.includes("e") || direction.includes("w");
                const isChangingHeight = direction.includes("n") || direction.includes("s");

                const newSize: Record<string, unknown> = { ...component.size };

                // Only update width if we're actually changing it
                if (isChangingWidth) {
                  // Preserve the original type (percentage vs pixels) when resizing
                  if (isWidthPercentage) {
                    // Calculate what percentage the new pixel width represents
                    const parentWidth = parentRect.width;
                    const percentage = Math.min(Math.max((nextWidth / parentWidth) * 100, 0), 100);
                    newSize.width = `${percentage.toFixed(1)}%`;
                  } else {
                    newSize.width = Math.round(nextWidth);
                  }
                }

                // Only update height if we're actually changing it
                if (isChangingHeight) {
                  // Preserve the original type (percentage vs pixels) when resizing
                  if (isHeightPercentage) {
                    // Calculate what percentage the new pixel height represents
                    const parentHeight = parentRect.height;
                    const percentage = Math.min(
                      Math.max((nextHeight / parentHeight) * 100, 0),
                      100,
                    );
                    newSize.height = `${percentage.toFixed(1)}%`;
                  } else {
                    newSize.height = Math.round(nextHeight);
                  }
                }

                // Only update position values that are actually being changed
                const newPosition: Record<string, unknown> = { ...component.position };

                if (direction.includes("w")) {
                  // When dragging west (left handle), set left position to move component
                  const leftPercentage = (nextLeft / parentRect.width) * 100;
                  newPosition.left = `${Math.min(Math.max(leftPercentage, 0), 100).toFixed(1)}%`;
                }
                if (direction.includes("n")) {
                  // When dragging north (top handle), set top position to move component
                  const topPercentage = (nextTop / parentRect.height) * 100;
                  newPosition.top = `${Math.min(Math.max(topPercentage, 0), 100).toFixed(1)}%`;
                }
                if (direction.includes("e") || direction.includes("w")) {
                  const rightPercent = (_nextRight / parentRect.width) * 100;
                  if (startRight !== undefined) {
                    newPosition.right =
                      typeof startRight === "string" && startRight.endsWith("%")
                        ? `${Math.min(Math.max(rightPercent, 0), 100).toFixed(1)}%`
                        : Math.max(_nextRight, 0);
                  }
                }
                if (direction.includes("s") || direction.includes("n")) {
                  const bottomPercent = (_nextBottom / parentRect.height) * 100;
                  if (startBottom !== undefined) {
                    newPosition.bottom =
                      typeof startBottom === "string" && startBottom.endsWith("%")
                        ? `${Math.min(Math.max(bottomPercent, 0), 100).toFixed(1)}%`
                        : Math.max(_nextBottom, 0);
                  }
                }

                updateSkinComponent(component.id, {
                  position: newPosition,
                  size: newSize,
                });
              } catch (error) {
                console.warn("Failed to update skin component during resize:", error);
              }
            };

            const onMouseUp = (_event: MouseEvent) => {
              setIsSkinComponentResizing(false);
              window.removeEventListener("mousemove", onMouseMove);
              window.removeEventListener("mouseup", onMouseUp);
              window.removeEventListener("pointerup", onMouseUp);
              window.removeEventListener("pointercancel", onMouseUp);
              window.removeEventListener("blur", onMouseUp);

              // Clear focus and active states from the specific resize handle that initiated the drag
              if (resizeHandle) {
                resizeHandle.blur();
              }
              // Also clear focus from document active element if it's a resize handle
              if (
                document.activeElement &&
                (document.activeElement as HTMLElement).classList?.contains("resize-handle")
              ) {
                (document.activeElement as HTMLElement).blur();
              }
            };

            setIsSkinComponentResizing(true);
            window.addEventListener("mousemove", onMouseMove);
            window.addEventListener("mouseup", onMouseUp);
            window.addEventListener("pointerup", onMouseUp);
            window.addEventListener("pointercancel", onMouseUp);
            window.addEventListener("blur", onMouseUp);
          };

        return (
          // biome-ignore lint/a11y/useSemanticElements: complex text component with click and keyboard handling
          <div
            key={component.id}
            className="component-container"
            data-skin-component
            data-component-id={component.id}
            data-parent-id={component.parentId}
            role="button"
            tabIndex={0}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              handleClick(e);
            }}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                e.stopPropagation();
                handleClick(e as unknown as React.MouseEvent);
              }
            }}
            onMouseDown={(e) => {
              handleMoveMouseDown(component, e);
            }}
            style={{
              ...baseStyleWithMargin,
              cursor: "pointer",
              zIndex: 1000,
              // Text-specific styling
              color: (safeStyle.color as string | undefined) || "#ffffff",
              fontSize: (safeStyle.fontSize as string | undefined) || "16px",
              fontFamily: (safeStyle.fontFamily as string | undefined) || "Arial, sans-serif",
              fontWeight: (safeStyle.fontWeight as string | undefined) || "normal",
              textAlign: (safeStyle.textAlign as string | undefined) || "left",
              lineHeight: (safeStyle.lineHeight as string | undefined) || "1.4",
              textDecoration: (safeStyle.textDecoration as string | undefined) || "none",
              textTransform: (safeStyle.textTransform as string | undefined) || "none",
              letterSpacing: (safeStyle.letterSpacing as string | undefined) || "normal",
              wordSpacing: (safeStyle.wordSpacing as string | undefined) || "normal",
              whiteSpace: (safeStyle.whiteSpace as string | undefined) || "pre-wrap",
              overflow: (safeStyle.overflow as string | undefined) || "visible",
              textOverflow: (safeStyle.textOverflow as string | undefined) || "clip",
              // Apply background color if set, otherwise transparent
              backgroundColor:
                resolveColorMixValue(safeStyle.backgroundColor as string | undefined) ||
                "transparent",
              border: "none",
              borderRadius: 0,
              // Ensure text component stays within viewport bounds
              maxWidth: "100%",
              maxHeight: "100%",
              // Apply padding to text components
              padding: safeStyle.padding ? `${safeStyle.padding}px` : undefined,
              paddingTop: safeStyle.paddingTop
                ? `${safeStyle.paddingTop}px`
                : undefined,
              paddingRight: safeStyle.paddingRight
                ? `${safeStyle.paddingRight}px`
                : undefined,
              paddingBottom: safeStyle.paddingBottom
                ? `${safeStyle.paddingBottom}px`
                : undefined,
              paddingLeft: safeStyle.paddingLeft
                ? `${safeStyle.paddingLeft}px`
                : undefined,
            }}
          >
            {selectedComponent?.id === component.id && !isPreviewMode && (
              <>
                {/* 8 resize handles for text components */}
                {/* biome-ignore lint/a11y/noStaticElementInteractions: resize handle with mouse-only drag */}
                <div
                  className="resize-handle"
                  style={{
                    position: "absolute",
                    top: -6,
                    left: -6,
                    width: 12,
                    height: 12,
                    backgroundColor: "#0D6CFF",
                    border: "2px solid white",
                    borderRadius: "50%",
                    cursor: "nwse-resize",
                    zIndex: 3000,
                    pointerEvents: "auto",
                    outline: "none",
                    userSelect: "none",
                  }}
                  onMouseDown={startResizeDrag("nw")}
                  title="Resize"
                />
                {/* biome-ignore lint/a11y/noStaticElementInteractions: resize handle with mouse-only drag */}
                <div
                  className="resize-handle"
                  style={{
                    position: "absolute",
                    top: -6,
                    left: "50%",
                    transform: "translateX(-50%)",
                    width: 12,
                    height: 12,
                    backgroundColor: "#0D6CFF",
                    border: "2px solid white",
                    borderRadius: "50%",
                    cursor: "ns-resize",
                    zIndex: 3000,
                    pointerEvents: "auto",
                    outline: "none",
                    userSelect: "none",
                  }}
                  onMouseDown={startResizeDrag("n")}
                  title="Resize"
                />
                {/* biome-ignore lint/a11y/noStaticElementInteractions: resize handle with mouse-only drag */}
                <div
                  className="resize-handle"
                  style={{
                    position: "absolute",
                    top: -6,
                    right: -6,
                    width: 12,
                    height: 12,
                    backgroundColor: "#0D6CFF",
                    border: "2px solid white",
                    borderRadius: "50%",
                    cursor: "nesw-resize",
                    zIndex: 3000,
                    pointerEvents: "auto",
                    outline: "none",
                    userSelect: "none",
                  }}
                  onMouseDown={startResizeDrag("ne")}
                  title="Resize"
                />
                {/* biome-ignore lint/a11y/noStaticElementInteractions: resize handle with mouse-only drag */}
                <div
                  className="resize-handle"
                  style={{
                    position: "absolute",
                    top: "50%",
                    left: -6,
                    transform: "translateY(-50%)",
                    width: 12,
                    height: 12,
                    backgroundColor: "#0D6CFF",
                    border: "2px solid white",
                    borderRadius: "50%",
                    cursor: "ew-resize",
                    zIndex: 3000,
                    pointerEvents: "auto",
                    outline: "none",
                    userSelect: "none",
                  }}
                  onMouseDown={startResizeDrag("w")}
                  title="Resize"
                />
                {/* biome-ignore lint/a11y/noStaticElementInteractions: resize handle with mouse-only drag */}
                <div
                  className="resize-handle"
                  style={{
                    position: "absolute",
                    top: "50%",
                    right: -6,
                    transform: "translateY(-50%)",
                    width: 12,
                    height: 12,
                    backgroundColor: "#0D6CFF",
                    border: "2px solid white",
                    borderRadius: "50%",
                    cursor: "ew-resize",
                    zIndex: 3000,
                    pointerEvents: "auto",
                    outline: "none",
                    userSelect: "none",
                  }}
                  onMouseDown={startResizeDrag("e")}
                  title="Resize"
                />
                {/* biome-ignore lint/a11y/noStaticElementInteractions: resize handle with mouse-only drag */}
                <div
                  className="resize-handle"
                  style={{
                    position: "absolute",
                    bottom: -6,
                    left: -6,
                    width: 12,
                    height: 12,
                    backgroundColor: "#0D6CFF",
                    border: "2px solid white",
                    borderRadius: "50%",
                    cursor: "nesw-resize",
                    zIndex: 3000,
                    pointerEvents: "auto",
                    outline: "none",
                    userSelect: "none",
                  }}
                  onMouseDown={startResizeDrag("sw")}
                  title="Resize"
                />
                {/* biome-ignore lint/a11y/noStaticElementInteractions: resize handle with mouse-only drag */}
                <div
                  className="resize-handle"
                  style={{
                    position: "absolute",
                    bottom: -6,
                    left: "50%",
                    transform: "translateX(-50%)",
                    width: 12,
                    height: 12,
                    backgroundColor: "#0D6CFF",
                    border: "2px solid white",
                    borderRadius: "50%",
                    cursor: "ns-resize",
                    zIndex: 3000,
                    pointerEvents: "auto",
                    outline: "none",
                    userSelect: "none",
                  }}
                  onMouseDown={startResizeDrag("s")}
                  title="Resize"
                />
                {/* biome-ignore lint/a11y/noStaticElementInteractions: resize handle with mouse-only drag */}
                <div
                  className="resize-handle"
                  style={{
                    position: "absolute",
                    bottom: -6,
                    right: -6,
                    width: 12,
                    height: 12,
                    backgroundColor: "#0D6CFF",
                    border: "2px solid white",
                    borderRadius: "50%",
                    cursor: "nwse-resize",
                    zIndex: 3000,
                    pointerEvents: "auto",
                    outline: "none",
                    userSelect: "none",
                  }}
                  onMouseDown={startResizeDrag("se")}
                  title="Resize"
                />
              </>
            )}

            {/* biome-ignore lint/a11y/useSemanticElements: text editor overlay with double-click support */}
            <div
              role="button"
              tabIndex={0}
              onClick={(e) => {
                e.stopPropagation();
                if (selectionEnabled && onComponentSelect) {
                  onComponentSelect(component);
                }
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  e.stopPropagation();
                  if (selectionEnabled && onComponentSelect) {
                    onComponentSelect(component);
                  }
                }
              }}
              onDoubleClick={(e) => {
                e.stopPropagation();
                startTextEditing(component);
              }}
              style={{
                width: "100%",
                height: "100%",
                pointerEvents: "auto",
                cursor: editingTextId === component.id ? "text" : "pointer",
                // Match the exact display behavior of the text element
                display: "block",
                position: "relative",
                // Apply the same text styling as the parent component
                color: component.style.color || "#ffffff",
                fontSize: component.style.fontSize || "16px",
                fontFamily: component.style.fontFamily || "Arial, sans-serif",
                fontWeight: component.style.fontWeight || "normal",
                textAlign: component.style.textAlign || "left",
                lineHeight: component.style.lineHeight || "1.4",
                textDecoration: component.style.textDecoration || "none",
                textTransform: component.style.textTransform || "none",
                letterSpacing: component.style.letterSpacing || "normal",
                wordSpacing: component.style.wordSpacing || "normal",
                whiteSpace: component.style.whiteSpace || "pre-wrap",
                overflow: component.style.overflow || "visible",
                textOverflow: component.style.textOverflow || "clip",
                backgroundColor:
                  resolveColorMixValue(component.style.backgroundColor as string | undefined) ||
                  "transparent",
                // Apply padding exactly like the parent
                padding: component.style.padding ? `${component.style.padding}px` : undefined,
                paddingTop: component.style.paddingTop
                  ? `${component.style.paddingTop}px`
                  : undefined,
                paddingRight: component.style.paddingRight
                  ? `${component.style.paddingRight}px`
                  : undefined,
                paddingBottom: component.style.paddingBottom
                  ? `${component.style.paddingBottom}px`
                  : undefined,
                paddingLeft: component.style.paddingLeft
                  ? `${component.style.paddingLeft}px`
                  : undefined,
              }}
            >
              {editingTextId === component.id ? (
                <textarea
                  value={editingTextValue}
                  onChange={(e) => setEditingTextValue(e.target.value)}
                  onBlur={() => saveTextEditing(component)}
                  onKeyDown={(e) => handleTextKeyDown(e, component)}
                  style={{
                    // Make textarea completely transparent and inherit all styling from parent
                    width: "100%",
                    height: "100%",
                    margin: 0,
                    // Inherit padding from parent
                    padding: "inherit",
                    background: "transparent",
                    outline: "none",
                    border: "none",
                    resize: "none",
                    // Inherit all text styling from parent container
                    color: "inherit",
                    fontSize: "inherit",
                    fontFamily: "inherit",
                    fontWeight: "inherit",
                    textAlign: "inherit",
                    lineHeight: "inherit",
                    textDecoration: "inherit",
                    textTransform: "inherit",
                    letterSpacing: "inherit",
                    wordSpacing: "inherit",
                    whiteSpace: "inherit",
                    overflow: "inherit",
                    textOverflow: "inherit",
                    backgroundColor: "transparent",
                    // Position to fill the parent exactly
                    position: "absolute",
                    top: 0,
                    left: 0,
                    // Ensure proper box sizing
                    boxSizing: "border-box",
                    // Remove any default textarea behavior
                    verticalAlign: "top",
                    display: "block",
                  }}
                />
              ) : (
                renderContent(component, "text")
              )}
            </div>
          </div>
        );
      }

      case "box": {
        const ContainerNode: React.FC<{ comp: SkinComponent; baseStyle: React.CSSProperties }> = ({
          comp,
          baseStyle,
        }) => {
          const containerRef = React.useRef<HTMLDivElement | null>(null);
          const [computedHeight, setComputedHeight] = React.useState<string | undefined>(undefined);
          const updateSkinComponent = React.useMemo(
            () => useEditorStore.getState().updateSkinComponent,
            [],
          );

          const startResizeDrag =
            (direction: "n" | "s" | "e" | "w" | "ne" | "nw" | "se" | "sw") =>
            (e: React.MouseEvent) => {
              e.stopPropagation();
              e.preventDefault();
              if (!containerRef.current) return;
              const element = containerRef.current;
              // Capture the resize handle element that initiated the drag
              const resizeHandle = e.currentTarget as HTMLElement;
              // Try to get the actual parent container that contains the skin components
              const parent =
                element.closest(".panel-container") ||
                (element.offsetParent as HTMLElement) ||
                element.parentElement;
              if (!parent) return;

              const startX = e.clientX;
              const startY = e.clientY;
              const startLeft = element.offsetLeft;
              const startTop = element.offsetTop;
              const startWidth = element.offsetWidth;
              const startHeight = element.offsetHeight;
              const startRight = comp.position.right;
              const startBottom = comp.position.bottom;
              const parentRect = parent.getBoundingClientRect();
              const minSize = 8;

              const onMouseMove = (ev: MouseEvent) => {
                const dx = ev.clientX - startX;
                const dy = ev.clientY - startY;

                const startRightEdge = startLeft + startWidth;
                const startBottomEdge = startTop + startHeight;

                let nextLeft = startLeft;
                let nextTop = startTop;
                let nextWidth = startWidth;
                let nextHeight = startHeight;

                // Horizontal resize: keep opposite edge fixed when min size reached
              if (direction.includes("e")) {
                const maxWidth = parentRect.width - startLeft;
                nextWidth = Math.max(minSize, Math.min(startWidth + dx, maxWidth));
              }
                if (direction.includes("w")) {
                  const proposedLeft = startLeft + dx;
                  const maxLeft = startRightEdge - minSize;
                  const boundedLeft = Math.max(0, Math.min(proposedLeft, maxLeft));
                  nextLeft = boundedLeft;
                  nextWidth = startRightEdge - boundedLeft;
                }

                // Vertical resize: keep opposite edge fixed when min size reached
                if (direction.includes("s")) {
                  const maxHeight = parentRect.height - startTop;
                  nextHeight = Math.max(minSize, Math.min(startHeight + dy, maxHeight));
                }
                if (direction.includes("n")) {
                  const proposedTop = startTop + dy;
                  const maxTop = startBottomEdge - minSize;
                  const boundedTop = Math.max(0, Math.min(proposedTop, maxTop));
                  nextTop = boundedTop;
                  nextHeight = startBottomEdge - boundedTop;
                }

                // Derive right/bottom from computed box to avoid flipping when clamped
                const _nextRight = parentRect.width - (nextLeft + nextWidth);
                const _nextBottom = parentRect.height - (nextTop + nextHeight);

                try {
                  // Only update dimensions that are actually being changed
                  const originalWidth = comp.size.width;
                  const originalHeight = comp.size.height;
                  const isWidthPercentage =
                    typeof originalWidth === "string" &&
                    (originalWidth.endsWith("%") || originalWidth === "auto");
                  const isHeightPercentage =
                    typeof originalHeight === "string" &&
                    (originalHeight.endsWith("%") || originalHeight === "auto");

                  // Check which dimensions are being changed based on direction
                  const isChangingWidth = direction.includes("e") || direction.includes("w");
                  const isChangingHeight = direction.includes("n") || direction.includes("s");

                  // Handle aspect ratio constraints during resize
                  if (comp.size.aspectRatio) {
                    const aspectRatio = comp.size.aspectRatio;
                    const [widthRatio, heightRatio] = aspectRatio.split("/").map(Number);
                    if (
                      widthRatio &&
                      heightRatio &&
                      !Number.isNaN(widthRatio) &&
                      !Number.isNaN(heightRatio)
                    ) {
                      const targetRatio = widthRatio / heightRatio;
                      if (isChangingWidth && !isChangingHeight) {
                        nextHeight = nextWidth / targetRatio;
                      } else if (isChangingHeight && !isChangingWidth) {
                        nextWidth = nextHeight * targetRatio;
                      } else if (isChangingWidth && isChangingHeight) {
                        nextHeight = nextWidth / targetRatio;
                      }
                    }
                  }

                  const newSize: Record<string, unknown> = { ...comp.size };

                  // Only update width if we're actually changing it
                  if (isChangingWidth) {
                    // Preserve the original type (percentage vs pixels) when resizing
                    if (isWidthPercentage) {
                      // Calculate what percentage the new pixel width represents
                      const parentWidth = parentRect.width;
                      const percentage = Math.min(
                        Math.max((nextWidth / parentWidth) * 100, 0),
                        100,
                      );
                      newSize.width = `${percentage.toFixed(1)}%`;
                    } else {
                      newSize.width = Math.round(nextWidth);
                    }
                  }

                  // Only update height if we're actually changing it
                  if (isChangingHeight) {
                    // Preserve the original type (percentage vs pixels) when resizing
                    if (isHeightPercentage) {
                      // Calculate what percentage the new pixel height represents
                      const parentHeight = parentRect.height;
                      const percentage = Math.min(
                        Math.max((nextHeight / parentHeight) * 100, 0),
                        100,
                      );
                      newSize.height = `${percentage.toFixed(1)}%`;
                    } else {
                      newSize.height = Math.round(nextHeight);
                    }
                  }

                  // Only update position values that are actually being changed
                  const newPosition: Record<string, unknown> = { ...comp.position };

                  if (direction.includes("w")) {
                    // When dragging west (left handle), set left position to move component
                    const leftPercentage = (nextLeft / parentRect.width) * 100;
                    newPosition.left = `${Math.min(Math.max(leftPercentage, 0), 100).toFixed(1)}%`;
                    // Don't clear right positioning - keep it for east handle to work properly
                    // newPosition.right = undefined;
                  }
                  if (direction.includes("n")) {
                    // When dragging north (top handle), set top position to move component
                    const topPercentage = (nextTop / parentRect.height) * 100;
                    newPosition.top = `${Math.min(Math.max(topPercentage, 0), 100).toFixed(1)}%`;
                    // Don't clear bottom positioning - keep it for south handle to work properly
                    // newPosition.bottom = undefined;
                  }
                  if (direction.includes("e") || direction.includes("w")) {
                    const rightPercent = (_nextRight / parentRect.width) * 100;
                    if (startRight !== undefined) {
                      newPosition.right =
                        typeof startRight === "string" && startRight.endsWith("%")
                          ? `${Math.min(Math.max(rightPercent, 0), 100).toFixed(1)}%`
                          : Math.max(_nextRight, 0);
                    }
                  }
                  if (direction.includes("s") || direction.includes("n")) {
                    const bottomPercent = (_nextBottom / parentRect.height) * 100;
                    if (startBottom !== undefined) {
                      newPosition.bottom =
                        typeof startBottom === "string" && startBottom.endsWith("%")
                          ? `${Math.min(Math.max(bottomPercent, 0), 100).toFixed(1)}%`
                          : Math.max(_nextBottom, 0);
                    }
                  }

                  updateSkinComponent(comp.id, {
                    position: newPosition,
                    size: newSize,
                  });
                } catch (error) {
                  console.warn("Failed to update skin component during resize:", error);
                }
              };

              const onMouseUp = (_event: MouseEvent) => {
                setIsSkinComponentResizing(false);
                window.removeEventListener("mousemove", onMouseMove);
                window.removeEventListener("mouseup", onMouseUp);
                window.removeEventListener("pointerup", onMouseUp);
                window.removeEventListener("pointercancel", onMouseUp);
                window.removeEventListener("blur", onMouseUp);

                // Clear focus and active states from the specific resize handle that initiated the drag
                if (resizeHandle) {
                  resizeHandle.blur();
                }
                // Also clear focus from document active element if it's a resize handle
                if (
                  document.activeElement &&
                  (document.activeElement as HTMLElement).classList?.contains("resize-handle")
                ) {
                  (document.activeElement as HTMLElement).blur();
                }
              };

              setIsSkinComponentResizing(true);
              window.addEventListener("mousemove", onMouseMove);
              window.addEventListener("mouseup", onMouseUp);
              window.addEventListener("pointerup", onMouseUp);
              window.addEventListener("pointercancel", onMouseUp);
              window.addEventListener("blur", onMouseUp);
            };

          React.useLayoutEffect(() => {
            // Always calculate height consistently regardless of selection state
            // Don't use early returns that can cause inconsistent behavior

            if (comp.size.height !== "auto") {
              // For percentage heights, we need to calculate the actual pixel value
              if (typeof comp.size.height === "string" && comp.size.height.endsWith("%")) {
                const parent = containerRef.current?.parentElement;
                if (parent) {
                  const parentHeight = parent.offsetHeight;
                  const percentage = parseFloat(comp.size.height as string);
                  if (!Number.isNaN(percentage)) {
                    setComputedHeight(`${Math.round((percentage / 100) * parentHeight)}px`);
                    return;
                  }
                }
              }
              // For pixel values, use as-is
              setComputedHeight(
                typeof comp.size.height === "number" ? `${comp.size.height}px` : comp.size.height,
              );
              return;
            }

            // Auto-height calculation - must be consistent between selected/unselected states
            const _hasTextContent = !!comp.content?.text && comp.content.text !== "";

            // Find child components by both data-parent-id and direct DOM children
            const childEls =
              containerRef.current?.querySelectorAll<HTMLElement>(
                `[data-parent-id='${comp.id}']`,
              ) ?? [];
            const directChildren = Array.from(containerRef.current?.children ?? []).filter(
              (child) =>
                child !== containerRef.current?.querySelector(".component-container") && // Exclude resize handles
                !(child as HTMLElement).classList?.contains("resize-handle"), // Exclude resize handles by class
            );

            const _hasChildComponents = childEls.length > 0 || directChildren.length > 0;

            // Check if any child components have auto height
            const hasAutoHeightChildren = components
              .filter((c) => c.parentId === comp.id)
              .some((child) => child.size.height === "auto");

            // For containers with auto-height children, we still need to calculate height
            // but we'll respect the child's natural height instead of forcing a minimum

            // Always calculate height consistently, don't use hardcoded fallbacks
            // that can cause size differences between selected/unselected states

            let maxBottom = 0;
            let maxHeight = 0;

            // Check child elements with data-parent-id
            childEls.forEach((el) => {
              const bottom = el.offsetTop + el.offsetHeight;
              const height = el.offsetHeight;
              if (bottom > maxBottom) maxBottom = bottom;
              if (height > maxHeight) maxHeight = height;
            });

            // Check direct DOM children (for nested components)
            directChildren.forEach((child) => {
              if (child instanceof HTMLElement) {
                const bottom = child.offsetTop + child.offsetHeight;
                const height = child.offsetHeight;
                if (bottom > maxBottom) maxBottom = bottom;
                if (height > maxHeight) maxHeight = height;
              }
            });

            // Include container vertical padding
            const container = containerRef.current;
            if (!container) {
              return;
            }
            const style = window.getComputedStyle(container);
            const padTop = parseInt(style.paddingTop || "0", 10) || 0;
            const padBottom = parseInt(style.paddingBottom || "0", 10) || 0;

            // Calculate final height
            let finalHeight = Math.max(maxBottom, maxHeight) + padTop + padBottom;

            // Add extra height padding for text components to give them breathing room
            if (hasAutoHeightChildren) {
              // Check if any of the auto-height children are text components
              const hasTextChildren = components
                .filter((c) => c.parentId === comp.id && c.size.height === "auto")
                .some((child) => child.type === "box" && child.content?.text);

              if (hasTextChildren) {
                // Add extra padding for text components (top and bottom)
                finalHeight += 16; // 8px top + 8px bottom for comfortable text spacing
              }
            }

            // Ensure consistent minimum height regardless of selection state
            // Use a consistent minimum height for all auto-height boxes
            if (finalHeight < 40) {
              finalHeight = 40; // Always use 40px minimum for consistency
            }

            // Always set computed height consistently
            setComputedHeight(finalHeight > 0 ? `${finalHeight}px` : "40px");
          }, [comp]);

          return (
            // biome-ignore lint/a11y/useSemanticElements: complex box component with resize handles and click handling
            <div
              ref={containerRef}
              key={comp.id}
              className="component-container"
              data-skin-component
              data-component-id={comp.id}
              data-parent-id={comp.parentId}
              role="button"
              tabIndex={0}
              onClickCapture={(e) => {
                const isModifierPressed = e.metaKey || e.ctrlKey;
                const shouldTriggerActions = previewMode || isModifierPressed;
                if (!shouldTriggerActions) return;
                if (!hasActions) {
                  console.log("Box click ignored (no actions):", {
                    componentId: comp.id,
                    componentType: comp.type,
                    isPreviewMode: previewMode,
                    isModifierPressed,
                  });
                  return;
                }

                const targetEl = e.target as HTMLElement;
                const targetComponentEl = targetEl.closest("[data-component-id]");
                const targetId = targetComponentEl?.getAttribute("data-component-id");
                if (targetId && targetId !== comp.id) {
                  const targetComp = components.find((c) => c.id === targetId);
                  if (targetComp && componentHasActions(targetComp)) {
                    console.log("Box action skipped (child has actions):", {
                      componentId: comp.id,
                      childId: targetId,
                    });
                    return;
                  }
                }

                handleClick(e as unknown as React.MouseEvent);
              }}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
              onClick={(e) => {
                if (selectionEnabled && onComponentSelect) {
                  e.stopPropagation();
                  onComponentSelect(comp);
                }
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  e.stopPropagation();
                  handleClick(e as unknown as React.MouseEvent);
                }
              }}
            onMouseDown={(e) => {
              // Only stop propagation if the target is not a resize handle
              if (!(e.target as HTMLElement).classList?.contains("resize-handle")) {
                handleMoveMouseDown(comp, e);
              }
            }}
              style={{
                ...baseStyle,
                cursor: "pointer", // Ensure cursor shows it's clickable
                backgroundColor: comp.style.backgroundColor || "rgba(255, 255, 255, 0.33)",
                borderRadius:
                  comp.style.borderRadius === 0 ? "0px" : comp.style.borderRadius || "0px",
                // Border properties
                borderStyle: comp.style.borderStyle || "none",
                borderColor: comp.style.borderColor || "#000000",
                borderWidth:
                  comp.style.borderWidth !== undefined && comp.style.borderWidth !== null
                    ? `${comp.style.borderWidth}px`
                    : comp.style.borderTopWidth !== undefined ||
                        comp.style.borderRightWidth !== undefined ||
                        comp.style.borderBottomWidth !== undefined ||
                        comp.style.borderLeftWidth !== undefined
                      ? undefined
                      : comp.style.borderStyle && comp.style.borderStyle !== "none"
                        ? "1px"
                        : undefined,
                borderTopWidth:
                  comp.style.borderTopWidth !== undefined && comp.style.borderTopWidth !== null
                    ? `${comp.style.borderTopWidth}px`
                    : undefined,
                borderRightWidth:
                  comp.style.borderRightWidth !== undefined && comp.style.borderRightWidth !== null
                    ? `${comp.style.borderRightWidth}px`
                    : undefined,
                borderBottomWidth:
                  comp.style.borderBottomWidth !== undefined && comp.style.borderBottomWidth !== null
                    ? `${comp.style.borderBottomWidth}px`
                    : undefined,
                borderLeftWidth:
                  comp.style.borderLeftWidth !== undefined && comp.style.borderLeftWidth !== null
                    ? `${comp.style.borderLeftWidth}px`
                    : undefined,
                borderTopLeftRadius: comp.style.borderTopLeftRadius
                  ? `${comp.style.borderTopLeftRadius}px`
                  : undefined,
                borderTopRightRadius: comp.style.borderTopRightRadius
                  ? `${comp.style.borderTopRightRadius}px`
                  : undefined,
                borderBottomLeftRadius: comp.style.borderBottomLeftRadius
                  ? `${comp.style.borderBottomLeftRadius}px`
                  : undefined,
                borderBottomRightRadius: comp.style.borderBottomRightRadius
                  ? `${comp.style.borderBottomRightRadius}px`
                  : undefined,
                padding: comp.style.padding
                  ? typeof comp.style.padding === "string" && comp.style.padding.endsWith("%")
                    ? comp.style.padding
                    : `${comp.style.padding}px`
                  : undefined,
                paddingTop: comp.style.paddingTop
                  ? typeof comp.style.paddingTop === "string" && comp.style.paddingTop.endsWith("%")
                    ? comp.style.paddingTop
                    : `${comp.style.paddingTop}px`
                  : undefined,
                paddingRight: comp.style.paddingRight
                  ? typeof comp.style.paddingRight === "string" &&
                    comp.style.paddingRight.endsWith("%")
                    ? comp.style.paddingRight
                    : `${comp.style.paddingRight}px`
                  : undefined,
                paddingBottom: comp.style.paddingBottom
                  ? typeof comp.style.paddingBottom === "string" &&
                    comp.style.paddingBottom.endsWith("%")
                    ? comp.style.paddingBottom
                    : `${comp.style.paddingBottom}px`
                  : undefined,
                paddingLeft: comp.style.paddingLeft
                  ? typeof comp.style.paddingLeft === "string" &&
                    comp.style.paddingLeft.endsWith("%")
                    ? comp.style.paddingLeft
                    : `${comp.style.paddingLeft}px`
                  : undefined,
                aspectRatio: comp.size.aspectRatio || undefined,
                // Always allow pointer events for component selection in editor
                pointerEvents: "auto",
                // Override height with computed height for auto-height components
                height:
                  comp.size.height === "auto" && computedHeight && !comp.size.aspectRatio
                    ? computedHeight
                    : baseStyle.height,
                // Ensure minimum height for clickability
                minHeight: "40px",
              }}
            >
              {selectedComponent?.id === comp.id && !isPreviewMode && (
                <>
                  {/* 8 resize handles */}
                  {/* biome-ignore lint/a11y/noStaticElementInteractions: resize handle with mouse-only drag */}
                  <div
                    className="resize-handle"
                    style={{
                      position: "absolute",
                      top: -6,
                      left: -6,
                      width: 12,
                      height: 12,
                      backgroundColor: "#0D6CFF",
                      border: "2px solid white",
                      borderRadius: "50%",
                      cursor: "nwse-resize",
                    zIndex: 3000,
                      pointerEvents: "auto",
                      outline: "none",
                      userSelect: "none",
                    }}
                    onMouseDown={startResizeDrag("nw")}
                    title="Resize"
                  />
                  {/* biome-ignore lint/a11y/noStaticElementInteractions: resize handle with mouse-only drag */}
                  <div
                    className="resize-handle"
                    style={{
                      position: "absolute",
                      top: -6,
                      left: "50%",
                      transform: "translateX(-50%)",
                      width: 12,
                      height: 12,
                      backgroundColor: "#0D6CFF",
                      border: "2px solid white",
                      borderRadius: "50%",
                      cursor: "ns-resize",
                    zIndex: 3000,
                      pointerEvents: "auto",
                      outline: "none",
                      userSelect: "none",
                    }}
                    onMouseDown={startResizeDrag("n")}
                    title="Resize"
                  />
                  {/* biome-ignore lint/a11y/noStaticElementInteractions: resize handle with mouse-only drag */}
                  <div
                    className="resize-handle"
                    style={{
                      position: "absolute",
                      top: -6,
                      right: -6,
                      width: 12,
                      height: 12,
                      backgroundColor: "#0D6CFF",
                      border: "2px solid white",
                      borderRadius: "50%",
                      cursor: "nesw-resize",
                    zIndex: 3000,
                      pointerEvents: "auto",
                      outline: "none",
                      userSelect: "none",
                    }}
                    onMouseDown={startResizeDrag("ne")}
                    title="Resize"
                  />
                  {/* biome-ignore lint/a11y/noStaticElementInteractions: resize handle with mouse-only drag */}
                  <div
                    className="resize-handle"
                    style={{
                      position: "absolute",
                      top: "50%",
                      left: -6,
                      transform: "translateY(-50%)",
                      width: 12,
                      height: 12,
                      backgroundColor: "#0D6CFF",
                      border: "2px solid white",
                      borderRadius: "50%",
                      cursor: "ew-resize",
                    zIndex: 3000,
                      pointerEvents: "auto",
                      outline: "none",
                      userSelect: "none",
                    }}
                    onMouseDown={startResizeDrag("w")}
                    title="Resize"
                  />
                  {/* biome-ignore lint/a11y/noStaticElementInteractions: resize handle with mouse-only drag */}
                  <div
                    className="resize-handle"
                    style={{
                      position: "absolute",
                      top: "50%",
                      right: -6,
                      transform: "translateY(-50%)",
                      width: 12,
                      height: 12,
                      backgroundColor: "#0D6CFF",
                      border: "2px solid white",
                      borderRadius: "50%",
                      cursor: "ew-resize",
                    zIndex: 3000,
                      pointerEvents: "auto",
                      outline: "none",
                      userSelect: "none",
                    }}
                    onMouseDown={startResizeDrag("e")}
                    title="Resize"
                  />
                  {/* biome-ignore lint/a11y/noStaticElementInteractions: resize handle with mouse-only drag */}
                  <div
                    className="resize-handle"
                    style={{
                      position: "absolute",
                      bottom: -6,
                      left: -6,
                      width: 12,
                      height: 12,
                      backgroundColor: "#0D6CFF",
                      border: "2px solid white",
                      borderRadius: "50%",
                      cursor: "nesw-resize",
                    zIndex: 3000,
                      pointerEvents: "auto",
                      outline: "none",
                      userSelect: "none",
                    }}
                    onMouseDown={startResizeDrag("sw")}
                    title="Resize"
                  />
                  {/* biome-ignore lint/a11y/noStaticElementInteractions: resize handle with mouse-only drag */}
                  <div
                    className="resize-handle"
                    style={{
                      position: "absolute",
                      bottom: -6,
                      left: "50%",
                      transform: "translateX(-50%)",
                      width: 12,
                      height: 12,
                      backgroundColor: "#0D6CFF",
                      border: "2px solid white",
                      borderRadius: "50%",
                      cursor: "ns-resize",
                    zIndex: 3000,
                      pointerEvents: "auto",
                      outline: "none",
                      userSelect: "none",
                    }}
                    onMouseDown={startResizeDrag("s")}
                    title="Resize"
                  />
                  {/* biome-ignore lint/a11y/noStaticElementInteractions: resize handle with mouse-only drag */}
                  <div
                    className="resize-handle"
                    style={{
                      position: "absolute",
                      bottom: -6,
                      right: -6,
                      width: 12,
                      height: 12,
                      backgroundColor: "#0D6CFF",
                      border: "2px solid white",
                      borderRadius: "50%",
                      cursor: "nwse-resize",
                    zIndex: 3000,
                      pointerEvents: "auto",
                      outline: "none",
                      userSelect: "none",
                    }}
                    onMouseDown={startResizeDrag("se")}
                    title="Resize"
                  />
                </>
              )}

              {renderContent(comp, "text") && (
                // biome-ignore lint/a11y/useSemanticElements: text content container with click handler
                <div
                  className="text-white text-sm"
                  data-parent-id={comp.id}
                  role="button"
                  tabIndex={0}
                  style={{
                    width: "100%",
                    height: comp.size.height === "auto" ? "auto" : "100%",
                    display: "flex",
                    alignItems: (comp.style.alignItems as string) || "center",
                    justifyContent: (comp.style.justifyContent as string) || "flex-start",
                    paddingLeft: comp.style.paddingLeft || 0,
                    paddingRight: comp.style.paddingRight || 0,
                    paddingTop: comp.style.paddingTop || 0,
                    paddingBottom: comp.style.paddingBottom || 0,
                    pointerEvents: "auto", // Allow clicks to be handled by this component
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (selectionEnabled && onComponentSelect) {
                      onComponentSelect(comp);
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      e.stopPropagation();
                      if (selectionEnabled && onComponentSelect) {
                        onComponentSelect(comp);
                      }
                    }
                  }}
                >
                  {renderContent(comp, "text")}
                </div>
              )}
              {/* Render nested components with proper parent ID */}
              {(() => {
                const children = components
                  .filter((c) => c.parentId === comp.id)
                  .sort((a, b) => b.zIndex - a.zIndex);
                const hasExplicitPosition = (child: SkinComponent) =>
                  child.position?.left !== undefined ||
                  child.position?.right !== undefined ||
                  child.position?.top !== undefined ||
                  child.position?.bottom !== undefined;
                const flowChildren = children.filter((child) => !hasExplicitPosition(child));
                const positionedChildren = children.filter((child) => hasExplicitPosition(child));
                const hasFlexConfig =
                  comp.style.justifyContent ||
                  comp.style.alignItems ||
                  comp.style.flexDirection ||
                  comp.style.rowGap ||
                  comp.style.columnGap ||
                  comp.style.gap;
                const allTextChildren =
                  flowChildren.length > 0 && flowChildren.every((child) => child.type === "text");
                const shouldAutoFlow = flowChildren.length > 0 && !hasFlexConfig;
                const flexDirection =
                  (comp.style.flexDirection as string) ||
                  (shouldAutoFlow ? (allTextChildren ? "row" : "column") : "column");
                const justifyContent =
                  (comp.style.justifyContent as string) || (shouldAutoFlow ? "flex-start" : "flex-start");
                const alignItems =
                  (comp.style.alignItems as string) ||
                  (shouldAutoFlow ? (allTextChildren ? "baseline" : "center") : "center");
                const hasAlignment = hasFlexConfig || shouldAutoFlow;
                if (hasAlignment && flowChildren.length > 0) {
                  return (
                    <>
                      <div
                        style={{
                          width: "100%",
                          height: comp.size.height === "auto" ? undefined : "100%",
                          display: "flex",
                          flexDirection: flexDirection as React.CSSProperties["flexDirection"],
                          justifyContent,
                          alignItems: alignItems as React.CSSProperties["alignItems"],
                          columnGap: comp.style.columnGap != null ? `${comp.style.columnGap}px` : undefined,
                          rowGap: comp.style.rowGap != null ? `${comp.style.rowGap}px` : undefined,
                          pointerEvents: "auto",
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          // Selection is handled by onClickCapture at the root level
                        }}
                      >
                        {flowChildren.map((child) => {
                          const childWithParent = { ...child, parentId: comp.id };
                          return (
                            <React.Fragment key={child.id}>
                              {renderComponent(childWithParent, { flexChild: true })}
                            </React.Fragment>
                          );
                        })}
                      </div>
                      {positionedChildren.map((child) => {
                        const childWithParent = { ...child, parentId: comp.id };
                        return (
                          <React.Fragment key={child.id}>
                            {renderComponent(childWithParent)}
                          </React.Fragment>
                        );
                      })}
                    </>
                  );
                }
                return children.map((child) => {
                  // Ensure child components have the correct parent ID for height calculation
                  const childWithParent = {
                    ...child,
                    parentId: comp.id,
                  };
                  return (
                    <React.Fragment key={child.id}>
                      {renderComponent(childWithParent)}
                    </React.Fragment>
                  );
                });
              })()}
            </div>
          );
        };

        return <ContainerNode comp={component} baseStyle={baseStyleWithMargin} />;
      }

      case "tabs": {
        const tabs = Array.isArray(component.content?.tabs)
          ? (component.content.tabs as Array<{ id: string; label: string }>)
          : [];
        const activeTabId =
          (component.content?.activeTabId as string | undefined) || tabs[0]?.id;
        const tabStyle = (component.content?.tabStyle as Record<string, unknown>) || {};
        const tabTextColor = (tabStyle.textColor as string) || "rgba(255,255,255,0.8)";
        const tabActiveTextColor = (tabStyle.activeTextColor as string) || "#ffffff";
        const tabBgColor = (tabStyle.backgroundColor as string) || "rgba(255,255,255,0.06)";
        const tabActiveBgColor = (tabStyle.activeBackgroundColor as string) || "rgba(59,130,246,0.3)";
        const tabFontSize =
          typeof tabStyle.fontSize === "number" ? tabStyle.fontSize : 12;
        const tabFontWeight =
          typeof tabStyle.fontWeight === "number" || typeof tabStyle.fontWeight === "string"
            ? tabStyle.fontWeight
            : 500;
        const tabPaddingX =
          typeof tabStyle.paddingX === "number" ? tabStyle.paddingX : 8;
        const tabPaddingY =
          typeof tabStyle.paddingY === "number" ? tabStyle.paddingY : 4;
        const tabBorderRadius =
          typeof tabStyle.borderRadius === "number" ? tabStyle.borderRadius : 6;
        const activeChild = components
          .filter((c) => c.parentId === component.id)
          .sort((a, b) => b.zIndex - a.zIndex)
          .find((child) => child.id === activeTabId);

        const updateSkinComponent = useEditorStore.getState().updateSkinComponent;
        const addSkinComponentWithData = useEditorStore.getState().addSkinComponentWithData;

        const setActiveTab = (tabId: string) => {
          updateSkinComponent(component.id, {
            content: { ...component.content, activeTabId: tabId },
          });
          if (selectionEnabled && onComponentSelect) {
            onComponentSelect(component);
          }
        };

        const handleAddTab = () => {
          const newTabId = crypto.randomUUID();
          const newLabel = `Tab ${tabs.length + 1}`;
          const nextTabs = [...tabs, { id: newTabId, label: newLabel }];
          updateSkinComponent(component.id, {
            content: { ...component.content, tabs: nextTabs, activeTabId: newTabId },
          });

          const siblings = components
            .filter((c) => c.parentId === component.id)
            .sort((a, b) => b.zIndex - a.zIndex);
          const newChild: SkinComponent = {
            id: newTabId,
            type: "box",
            name: `Tab Content ${tabs.length + 1}`,
            parentId: component.id,
            position: {},
            size: { width: "100%", height: "auto" },
            style: { backgroundColor: "transparent", borderRadius: 0 },
            content: {},
            visible: true,
            zIndex: siblings.length + 1,
            pointerEvents: true,
          };
          const reordered = [...siblings, newChild];
          reordered.forEach((sibling, index) => {
            const nextZ = reordered.length - index;
            if (sibling.id === newChild.id) {
              newChild.zIndex = nextZ;
              return;
            }
            if (sibling.zIndex !== nextZ) {
              updateSkinComponent(sibling.id, { zIndex: nextZ });
            }
          });
          addSkinComponentWithData(newChild);
        };

        return (
          <div
            key={component.id}
            className="component-container"
            data-skin-component
            data-component-id={component.id}
            data-parent-id={component.parentId}
            role="button"
            tabIndex={0}
            onClick={(e) => {
              const target = e.target as HTMLElement;
              if (target.closest("[data-tabs-control]") || target.closest("[data-tabs-content]")) {
                return;
              }
              if (selectionEnabled && onComponentSelect) {
                e.stopPropagation();
                onComponentSelect(component);
              }
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                e.stopPropagation();
                if (selectionEnabled && onComponentSelect) {
                  onComponentSelect(component);
                }
              }
            }}
            onMouseDown={(e) => {
              const target = e.target as HTMLElement;
              if (
                target.closest("[data-tabs-control]") ||
                target.closest("[data-tabs-content]") ||
                target.closest("button, input, textarea")
              ) {
                return;
              }
              if (!(target as HTMLElement).classList?.contains("resize-handle")) {
                handleMoveMouseDown(component, e);
              }
            }}
            style={{
              ...baseStyleWithMargin,
              cursor: "pointer",
              backgroundColor:
                resolveColorMixValue(component.style.backgroundColor as string | undefined) ||
                "transparent",
              borderRadius:
                component.style.borderRadius === 0 ? "0px" : component.style.borderRadius || "0px",
              borderStyle: component.style.borderStyle || "none",
              borderColor: component.style.borderColor || "#000000",
              borderWidth:
                component.style.borderWidth !== undefined && component.style.borderWidth !== null
                  ? `${component.style.borderWidth}px`
                  : component.style.borderStyle && component.style.borderStyle !== "none"
                    ? "1px"
                    : undefined,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                padding: "6px 8px",
                borderBottom: "1px solid rgba(255,255,255,0.08)",
                position: "relative",
                zIndex: 2,
                pointerEvents: "auto",
              }}
            >
              {tabs.map((tab) => {
                const isActive = tab.id === activeTabId;
                return (
                  <button
                    data-tabs-control
                    key={tab.id}
                    type="button"
                    onPointerDown={(e) => {
                      if (e.button !== 0) return;
                      e.stopPropagation();
                      setActiveTab(tab.id);
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setActiveTab(tab.id);
                    }}
                    className="text-xs transition-colors"
                    style={{
                      color: isActive ? tabActiveTextColor : tabTextColor,
                      background: isActive ? tabActiveBgColor : tabBgColor,
                      fontSize: `${tabFontSize}px`,
                      fontWeight: tabFontWeight as React.CSSProperties["fontWeight"],
                      padding: `${tabPaddingY}px ${tabPaddingX}px`,
                      borderRadius: `${tabBorderRadius}px`,
                    }}
                  >
                    {tab.label}
                  </button>
                );
              })}
              {!isPreviewMode && (
                <button
                  data-tabs-control
                  type="button"
                  onPointerDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleAddTab();
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleAddTab();
                  }}
                  className="ml-1 rounded px-2 py-1 text-xs text-white/70 bg-white/5 hover:bg-white/5"
                  title="Add tab"
                >
                  +
                </button>
              )}
            </div>
            <div
              data-tabs-content
              style={{ padding: "8px", position: "relative", zIndex: 1, pointerEvents: "auto" }}
            >
              {activeChild && renderComponent(activeChild, { flexChild: true })}
            </div>
          </div>
        );
      }

      case "image": {
        const startResizeDrag =
          (direction: "n" | "s" | "e" | "w" | "ne" | "nw" | "se" | "sw") =>
          (e: React.MouseEvent) => {
            e.stopPropagation();
            e.preventDefault();
            const element = e.currentTarget.parentElement;
            if (!element) return;

            let parent = element.parentElement;
            while (parent && parent !== document.body) {
              const rect = parent.getBoundingClientRect();
              if (rect.width > 0 && rect.height > 0) {
                break;
              }
              parent = parent.parentElement;
            }
            if (!parent) return;

            const startX = e.clientX;
            const startY = e.clientY;
            const startLeft = element.offsetLeft;
            const startTop = element.offsetTop;
            const startWidth = element.offsetWidth;
            const startHeight = element.offsetHeight;
            const startRight = component.position.right;
            const startBottom = component.position.bottom;
            const parentRect = parent.getBoundingClientRect();
            const minSize = 8;

            const onMouseMove = (ev: MouseEvent) => {
              const dx = ev.clientX - startX;
              const dy = ev.clientY - startY;

              const startRightEdge = startLeft + startWidth;
              const startBottomEdge = startTop + startHeight;

              let nextLeft = startLeft;
              let nextTop = startTop;
              let nextWidth = startWidth;
              let nextHeight = startHeight;

                if (direction.includes("e")) {
                  const maxWidth = parentRect.width - startLeft;
                  nextWidth = Math.max(minSize, Math.min(startWidth + dx, maxWidth));
                }
              if (direction.includes("w")) {
                const proposedLeft = startLeft + dx;
                const maxLeft = startRightEdge - minSize;
                nextLeft = Math.min(proposedLeft, maxLeft);
                nextWidth = startRightEdge - nextLeft;
              }
              if (direction.includes("s")) {
                nextHeight = Math.max(minSize, startHeight + dy);
              }
              if (direction.includes("n")) {
                const proposedTop = startTop + dy;
                const maxTop = startBottomEdge - minSize;
                nextTop = Math.min(proposedTop, maxTop);
                nextHeight = startBottomEdge - nextTop;
              }

              const nextRight =
                startRight !== undefined
                  ? parentRect.width - (nextLeft + nextWidth)
                  : undefined;
              const nextBottom =
                startBottom !== undefined
                  ? parentRect.height - (nextTop + nextHeight)
                  : undefined;

              const updateSkinComponent = useEditorStore.getState().updateSkinComponent;
              updateSkinComponent(component.id, {
                size: {
                  ...component.size,
                  width: nextWidth,
                  height: nextHeight,
                },
                position: {
                  ...component.position,
                  left: nextLeft,
                  top: nextTop,
                  right:
                    startRight !== undefined
                      ? typeof startRight === "string" && startRight.endsWith("%")
                        ? `${Math.min(Math.max(((nextRight ?? 0) / parentRect.width) * 100, 0), 100).toFixed(1)}%`
                        : Math.max(nextRight ?? 0, 0)
                      : nextRight,
                  bottom:
                    startBottom !== undefined
                      ? typeof startBottom === "string" && startBottom.endsWith("%")
                        ? `${Math.min(Math.max(((nextBottom ?? 0) / parentRect.height) * 100, 0), 100).toFixed(1)}%`
                        : Math.max(nextBottom ?? 0, 0)
                      : nextBottom,
                },
              });
            };

            const onMouseUp = () => {
              document.removeEventListener("mousemove", onMouseMove);
              document.removeEventListener("mouseup", onMouseUp);
              window.removeEventListener("mouseup", onMouseUp);
              window.removeEventListener("pointerup", onMouseUp);
              window.removeEventListener("pointercancel", onMouseUp);
              window.removeEventListener("blur", onMouseUp);
            };

            document.addEventListener("mousemove", onMouseMove);
            document.addEventListener("mouseup", onMouseUp);
            window.addEventListener("mouseup", onMouseUp);
            window.addEventListener("pointerup", onMouseUp);
            window.addEventListener("pointercancel", onMouseUp);
            window.addEventListener("blur", onMouseUp);
          };

        const baseStyle: React.CSSProperties = {
          position: "absolute",
          left: component.position.left,
          top: component.position.top,
          right: component.position.right,
          bottom: component.position.bottom,
          width: component.size.width,
          height: component.size.height,
          minWidth: component.size.minWidth,
          minHeight: component.size.minHeight,
          maxWidth: component.size.maxWidth,
          maxHeight: component.size.maxHeight,
          aspectRatio: component.size.aspectRatio,
          zIndex: component.zIndex,
          pointerEvents: component.pointerEvents ? "auto" : "none",
          outline:
            selectedComponent?.id === component.id && !isPreviewMode ? "2px solid #0D6CFF" : "none",
          ...component.style,
        };

        // Calculate image properties outside of JSX to avoid hooks violation
        const imageSrc = (renderContent(component, "src") as string) || "";
        const imageAlt = (renderContent(component, "alt") as string) || "Image";

        return (
          <>
            {/* biome-ignore lint/a11y/useSemanticElements: image component with conditional click handler */}
            <div
              ref={(el) => {
                if (el) {
                  el.style.left =
                    typeof component.position.left === "number"
                      ? `${component.position.left}px`
                      : component.position.left || "auto";
                  el.style.top =
                    typeof component.position.top === "number"
                      ? `${component.position.top}px`
                      : component.position.top || "auto";
                  el.style.right =
                    typeof component.position.right === "number"
                      ? `${component.position.right}px`
                      : component.position.right || "auto";
                  el.style.bottom =
                    typeof component.position.bottom === "number"
                      ? `${component.position.bottom}px`
                      : component.position.bottom || "auto";
                  el.style.width =
                    typeof component.size.width === "number"
                      ? `${component.size.width}px`
                      : component.size.width || "auto";
                  el.style.height =
                    typeof component.size.height === "number"
                      ? `${component.size.height}px`
                      : component.size.height || "auto";
                  el.style.minWidth =
                    typeof component.size.minWidth === "number"
                      ? `${component.size.minWidth}px`
                      : component.size.minWidth || "auto";
                  el.style.minHeight =
                    typeof component.size.minHeight === "number"
                      ? `${component.size.minHeight}px`
                      : component.size.minHeight || "auto";
                  el.style.maxWidth =
                    typeof component.size.maxWidth === "number"
                      ? `${component.size.maxWidth}px`
                      : component.size.maxWidth || "auto";
                  el.style.maxHeight =
                    typeof component.size.maxHeight === "number"
                      ? `${component.size.maxHeight}px`
                      : component.size.maxHeight || "auto";
                  el.style.aspectRatio = component.size.aspectRatio || "auto";
                  el.style.zIndex = component.zIndex.toString();
                  el.style.pointerEvents = component.pointerEvents ? "auto" : "none";
                }
              }}
              style={baseStyleWithMargin}
              className={`skin-component ${selectedComponent?.id === component.id ? "selected" : ""}`}
              role="button"
              tabIndex={0}
              onMouseDown={(e) => handleMoveMouseDown(component, e)}
              onClick={(e) => {
                handleClick(e as unknown as React.MouseEvent);
              }}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  handleClick(e as unknown as React.MouseEvent);
                }
              }}
              data-skin-component
            >
              <img
                src={imageSrc}
                alt={imageAlt}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: component.content?.objectFit || "cover",
                  display: imageSrc ? "block" : "none",
                  // Apply border radius to match the container
                  borderRadius: component.style.borderRadius
                    ? `${component.style.borderRadius}px`
                    : component.style.borderTopLeftRadius ||
                        component.style.borderTopRightRadius ||
                        component.style.borderBottomLeftRadius ||
                        component.style.borderBottomRightRadius
                      ? `${component.style.borderTopLeftRadius || 0}px ${component.style.borderTopRightRadius || 0}px ${component.style.borderBottomRightRadius || 0}px ${component.style.borderBottomLeftRadius || 0}px`
                      : "0px",
                }}
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                }}
              />
              {!imageSrc && (
                <div
                  style={{
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                    border: "2px dashed rgba(255, 255, 255, 0.3)",
                    color: "rgba(255, 255, 255, 0.5)",
                    fontSize: "14px",
                    textAlign: "center",
                  }}
                >
                  No Image
                </div>
              )}

              {selectedComponent?.id === component.id && !isPreviewMode && (
                <>
                  {/* 8 resize handles for image components */}
                  {/* biome-ignore lint/a11y/noStaticElementInteractions: resize handle with mouse-only drag */}
                  <div
                    className="resize-handle resize-handle-nw"
                    style={{
                      position: "absolute",
                      top: -6,
                      left: -6,
                      width: 12,
                      height: 12,
                      backgroundColor: "#0D6CFF",
                      border: "2px solid white",
                      borderRadius: "50%",
                      cursor: "nwse-resize",
                    zIndex: 3000,
                      pointerEvents: "auto",
                      outline: "none",
                      userSelect: "none",
                    }}
                    onMouseDown={startResizeDrag("nw")}
                    title="Resize"
                  />
                  {/* biome-ignore lint/a11y/noStaticElementInteractions: resize handle with mouse-only drag */}
                  <div
                    className="resize-handle resize-handle-n"
                    style={{
                      position: "absolute",
                      top: -6,
                      left: "50%",
                      transform: "translateX(-50%)",
                      width: 12,
                      height: 12,
                      backgroundColor: "#0D6CFF",
                      border: "2px solid white",
                      borderRadius: "50%",
                      cursor: "ns-resize",
                    zIndex: 3000,
                      pointerEvents: "auto",
                      outline: "none",
                      userSelect: "none",
                    }}
                    onMouseDown={startResizeDrag("n")}
                    title="Resize"
                  />
                  {/* biome-ignore lint/a11y/noStaticElementInteractions: resize handle with mouse-only drag */}
                  <div
                    className="resize-handle resize-handle-ne"
                    style={{
                      position: "absolute",
                      top: -6,
                      right: -6,
                      width: 12,
                      height: 12,
                      backgroundColor: "#0D6CFF",
                      border: "2px solid white",
                      borderRadius: "50%",
                      cursor: "nesw-resize",
                    zIndex: 3000,
                      pointerEvents: "auto",
                      outline: "none",
                      userSelect: "none",
                    }}
                    onMouseDown={startResizeDrag("ne")}
                    title="Resize"
                  />
                  {/* biome-ignore lint/a11y/noStaticElementInteractions: resize handle with mouse-only drag */}
                  <div
                    className="resize-handle resize-handle-e"
                    style={{
                      position: "absolute",
                      top: "50%",
                      right: -6,
                      transform: "translateY(-50%)",
                      width: 12,
                      height: 12,
                      backgroundColor: "#0D6CFF",
                      border: "2px solid white",
                      borderRadius: "50%",
                      cursor: "ew-resize",
                    zIndex: 3000,
                      pointerEvents: "auto",
                      outline: "none",
                      userSelect: "none",
                    }}
                    onMouseDown={startResizeDrag("e")}
                    title="Resize"
                  />
                  {/* biome-ignore lint/a11y/noStaticElementInteractions: resize handle with mouse-only drag */}
                  <div
                    className="resize-handle resize-handle-se"
                    style={{
                      position: "absolute",
                      bottom: -6,
                      right: -6,
                      width: 12,
                      height: 12,
                      backgroundColor: "#0D6CFF",
                      border: "2px solid white",
                      borderRadius: "50%",
                      cursor: "nwse-resize",
                    zIndex: 3000,
                      pointerEvents: "auto",
                      outline: "none",
                      userSelect: "none",
                    }}
                    onMouseDown={startResizeDrag("se")}
                    title="Resize"
                  />
                  {/* biome-ignore lint/a11y/noStaticElementInteractions: resize handle with mouse-only drag */}
                  <div
                    className="resize-handle resize-handle-s"
                    style={{
                      position: "absolute",
                      bottom: -6,
                      left: "50%",
                      transform: "translateX(-50%)",
                      width: 12,
                      height: 12,
                      backgroundColor: "#0D6CFF",
                      border: "2px solid white",
                      borderRadius: "50%",
                      cursor: "ns-resize",
                    zIndex: 3000,
                      pointerEvents: "auto",
                      outline: "none",
                      userSelect: "none",
                    }}
                    onMouseDown={startResizeDrag("s")}
                    title="Resize"
                  />
                  {/* biome-ignore lint/a11y/noStaticElementInteractions: resize handle with mouse-only drag */}
                  <div
                    className="resize-handle resize-handle-sw"
                    style={{
                      position: "absolute",
                      bottom: -6,
                      left: -6,
                      width: 12,
                      height: 12,
                      backgroundColor: "#0D6CFF",
                      border: "2px solid white",
                      borderRadius: "50%",
                      cursor: "nesw-resize",
                    zIndex: 3000,
                      pointerEvents: "auto",
                      outline: "none",
                      userSelect: "none",
                    }}
                    onMouseDown={startResizeDrag("sw")}
                    title="Resize"
                  />
                  {/* biome-ignore lint/a11y/noStaticElementInteractions: resize handle with mouse-only drag */}
                  <div
                    className="resize-handle resize-handle-w"
                    style={{
                      position: "absolute",
                      top: "50%",
                      left: -6,
                      transform: "translateY(-50%)",
                      width: 12,
                      height: 12,
                      backgroundColor: "#0D6CFF",
                      border: "2px solid white",
                      borderRadius: "50%",
                      cursor: "ew-resize",
                    zIndex: 3000,
                      pointerEvents: "auto",
                      outline: "none",
                      userSelect: "none",
                    }}
                    onMouseDown={startResizeDrag("w")}
                    title="Resize"
                  />
                </>
              )}
            </div>
          </>
        );
      }

      case "icon": {
        const startResizeDrag =
          (direction: "n" | "s" | "e" | "w" | "ne" | "nw" | "se" | "sw") =>
          (e: React.MouseEvent) => {
            e.stopPropagation();
            e.preventDefault();
            const element = e.currentTarget.parentElement;
            if (!element) return;

            let parent = element.parentElement;
            while (parent && parent !== document.body) {
              const rect = parent.getBoundingClientRect();
              if (rect.width > 0 && rect.height > 0) {
                break;
              }
              parent = parent.parentElement;
            }
            if (!parent) return;

            const startX = e.clientX;
            const startY = e.clientY;
            const startLeft = element.offsetLeft;
            const startTop = element.offsetTop;
            const startWidth = element.offsetWidth;
            const startHeight = element.offsetHeight;

            const handleMouseMove = (e: MouseEvent) => {
              const deltaX = e.clientX - startX;
              const deltaY = e.clientY - startY;

              const minSize = 20;
              const startRightEdge = startLeft + startWidth;
              const startBottomEdge = startTop + startHeight;

              let newLeft = startLeft;
              let newTop = startTop;
              let newWidth = startWidth;
              let newHeight = startHeight;

              if (direction.includes("e")) {
                const maxWidth = parent.offsetWidth - startLeft;
                newWidth = Math.max(minSize, Math.min(startWidth + deltaX, maxWidth));
              }
              if (direction.includes("w")) {
                const proposedLeft = startLeft + deltaX;
                const maxLeft = startRightEdge - minSize;
                const boundedLeft = Math.max(0, Math.min(proposedLeft, maxLeft));
                newLeft = boundedLeft;
                newWidth = startRightEdge - boundedLeft;
              }
              if (direction.includes("s")) {
                const maxHeight = parent.offsetHeight - startTop;
                newHeight = Math.max(minSize, Math.min(startHeight + deltaY, maxHeight));
              }
              if (direction.includes("n")) {
                const proposedTop = startTop + deltaY;
                const maxTop = startBottomEdge - minSize;
                const boundedTop = Math.max(0, Math.min(proposedTop, maxTop));
                newTop = boundedTop;
                newHeight = startBottomEdge - boundedTop;
              }

              element.style.left = `${newLeft}px`;
              element.style.top = `${newTop}px`;
              element.style.width = `${newWidth}px`;
              element.style.height = `${newHeight}px`;
            };

            const handleMouseUp = () => {
              document.removeEventListener("mousemove", handleMouseMove);
              document.removeEventListener("mouseup", handleMouseUp);
              window.removeEventListener("mouseup", handleMouseUp);
              window.removeEventListener("pointerup", handleMouseUp);
              window.removeEventListener("pointercancel", handleMouseUp);
              window.removeEventListener("blur", handleMouseUp);

              // Update component with new dimensions
              const newWidth = element.offsetWidth;
              const newHeight = element.offsetHeight;
              const newLeft = element.offsetLeft;
              const newTop = element.offsetTop;

              onUpdateComponents(
                components.map((c) =>
                  c.id === component.id
                    ? {
                        ...c,
                        size: { ...c.size, width: newWidth, height: newHeight },
                        position: { ...c.position, left: newLeft, top: newTop },
                      }
                    : c,
                ),
              );
            };

            document.addEventListener("mousemove", handleMouseMove);
            document.addEventListener("mouseup", handleMouseUp);
            window.addEventListener("mouseup", handleMouseUp);
            window.addEventListener("pointerup", handleMouseUp);
            window.addEventListener("pointercancel", handleMouseUp);
            window.addEventListener("blur", handleMouseUp);
          };

        return (
          <>
            {/* biome-ignore lint/a11y/useSemanticElements: icon component with conditional click handler */}
            <div
              key={component.id}
              className={`absolute ${selectedComponent?.id === component.id ? "ring-2 ring-blue-500" : ""}`}
              data-component-id={component.id}
              data-parent-id={component.parentId}
              style={{
                ...baseStyleWithMargin,
                padding: component.padding
                  ? `${component.padding.top}px ${component.padding.right}px ${component.padding.bottom}px ${component.padding.left}px`
                  : undefined,
                aspectRatio: component.size.aspectRatio,
              }}
              role="button"
              tabIndex={0}
              onMouseDown={(e) => handleMoveMouseDown(component, e)}
              onClick={(e) => {
                handleClick(e as unknown as React.MouseEvent);
              }}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  handleClick(e as unknown as React.MouseEvent);
                }
              }}
              data-skin-component
            >
              {component.content?.lucideIcon ? (
                (() => {
                  const IconComp = LucideIcons[
                    component.content.lucideIcon as keyof typeof LucideIcons
                  ] as unknown as React.ComponentType<{
                    size?: number;
                    color?: string;
                    className?: string;
                    style?: React.CSSProperties;
                  }>;
                  if (!IconComp) return null;
                  return (
                    <div
                      style={{
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <IconComp
                        color={component.content?.color || "#ffffff"}
                        style={{ width: "100%", height: "100%" }}
                      />
                    </div>
                  );
                })()
              ) : component.content?.svg ? (
                (() => {
                  const sanitizedSvg = sanitizeSvgMarkup(String(component.content.svg || ""));
                  if (!sanitizedSvg) {
                    return null;
                  }
                  return (
                    <div
                      // biome-ignore lint/security/noDangerouslySetInnerHtml: SVG is sanitized with sanitizeSvgMarkup
                      dangerouslySetInnerHTML={{ __html: sanitizedSvg }}
                      style={{
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: component.content?.color || "#ffffff",
                      }}
                    />
                  );
                })()
              ) : component.content?.src ? (
                <img
                  src={component.content.src}
                  alt={component.content?.alt || "Icon"}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: component.content?.objectFit || "contain",
                    display: component.content?.src ? "block" : "none",
                  }}
                  onError={(e) => {
                    console.warn("Icon failed to load:", component.content?.src);
                    e.currentTarget.style.display = "none";
                  }}
                />
              ) : (
                <div
                  style={{
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                    border: "2px dashed rgba(255, 255, 255, 0.3)",
                    color: "rgba(255, 255, 255, 0.5)",
                    fontSize: "14px",
                    textAlign: "center",
                  }}
                >
                  No Icon
                </div>
              )}

              {selectedComponent?.id === component.id && !isPreviewMode && (
                <>
                  {/* biome-ignore lint/a11y/noStaticElementInteractions: resize handle with mouse-only drag */}
                  <div
                    className="resize-handle resize-handle-nw"
                    style={{
                      position: "absolute",
                      top: -6,
                      left: -6,
                      width: 12,
                      height: 12,
                      backgroundColor: "#0D6CFF",
                      border: "2px solid white",
                      borderRadius: "50%",
                      cursor: "nw-resize",
                    zIndex: 3000,
                      pointerEvents: "auto",
                      outline: "none",
                      userSelect: "none",
                    }}
                    onMouseDown={startResizeDrag("nw")}
                    title="Resize"
                  />
                  {/* biome-ignore lint/a11y/noStaticElementInteractions: resize handle with mouse-only drag */}
                  <div
                    className="resize-handle resize-handle-ne"
                    style={{
                      position: "absolute",
                      top: -6,
                      right: -6,
                      width: 12,
                      height: 12,
                      backgroundColor: "#0D6CFF",
                      border: "2px solid white",
                      borderRadius: "50%",
                      cursor: "ne-resize",
                    zIndex: 3000,
                      pointerEvents: "auto",
                      outline: "none",
                      userSelect: "none",
                    }}
                    onMouseDown={startResizeDrag("ne")}
                    title="Resize"
                  />
                  {/* biome-ignore lint/a11y/noStaticElementInteractions: resize handle with mouse-only drag */}
                  <div
                    className="resize-handle resize-handle-se"
                    style={{
                      position: "absolute",
                      bottom: -6,
                      right: -6,
                      width: 12,
                      height: 12,
                      backgroundColor: "#0D6CFF",
                      border: "2px solid white",
                      borderRadius: "50%",
                      cursor: "se-resize",
                    zIndex: 3000,
                      pointerEvents: "auto",
                      outline: "none",
                      userSelect: "none",
                    }}
                    onMouseDown={startResizeDrag("se")}
                    title="Resize"
                  />
                  {/* biome-ignore lint/a11y/noStaticElementInteractions: resize handle with mouse-only drag */}
                  <div
                    className="resize-handle resize-handle-sw"
                    style={{
                      position: "absolute",
                      bottom: -6,
                      left: -6,
                      width: 12,
                      height: 12,
                      backgroundColor: "#0D6CFF",
                      border: "2px solid white",
                      borderRadius: "50%",
                      cursor: "sw-resize",
                    zIndex: 3000,
                      pointerEvents: "auto",
                      outline: "none",
                      userSelect: "none",
                    }}
                    onMouseDown={startResizeDrag("sw")}
                    title="Resize"
                  />
                  {/* biome-ignore lint/a11y/noStaticElementInteractions: resize handle with mouse-only drag */}
                  <div
                    className="resize-handle resize-handle-n"
                    style={{
                      position: "absolute",
                      top: -6,
                      left: "50%",
                      transform: "translateX(-50%)",
                      width: 12,
                      height: 12,
                      backgroundColor: "#0D6CFF",
                      border: "2px solid white",
                      borderRadius: "50%",
                      cursor: "n-resize",
                    zIndex: 3000,
                      pointerEvents: "auto",
                      outline: "none",
                      userSelect: "none",
                    }}
                    onMouseDown={startResizeDrag("n")}
                    title="Resize"
                  />
                  {/* biome-ignore lint/a11y/noStaticElementInteractions: resize handle with mouse-only drag */}
                  <div
                    className="resize-handle resize-handle-s"
                    style={{
                      position: "absolute",
                      bottom: -6,
                      left: "50%",
                      transform: "translateX(-50%)",
                      width: 12,
                      height: 12,
                      backgroundColor: "#0D6CFF",
                      border: "2px solid white",
                      borderRadius: "50%",
                      cursor: "s-resize",
                    zIndex: 3000,
                      pointerEvents: "auto",
                      outline: "none",
                      userSelect: "none",
                    }}
                    onMouseDown={startResizeDrag("s")}
                    title="Resize"
                  />
                  {/* biome-ignore lint/a11y/noStaticElementInteractions: resize handle with mouse-only drag */}
                  <div
                    className="resize-handle resize-handle-e"
                    style={{
                      position: "absolute",
                      top: "50%",
                      right: -6,
                      transform: "translateY(-50%)",
                      width: 12,
                      height: 12,
                      backgroundColor: "#0D6CFF",
                      border: "2px solid white",
                      borderRadius: "50%",
                      cursor: "ew-resize",
                    zIndex: 3000,
                      pointerEvents: "auto",
                      outline: "none",
                      userSelect: "none",
                    }}
                    onMouseDown={startResizeDrag("e")}
                    title="Resize"
                  />
                  {/* biome-ignore lint/a11y/noStaticElementInteractions: resize handle with mouse-only drag */}
                  <div
                    className="resize-handle resize-handle-w"
                    style={{
                      position: "absolute",
                      top: "50%",
                      left: -6,
                      transform: "translateY(-50%)",
                      width: 12,
                      height: 12,
                      backgroundColor: "#0D6CFF",
                      border: "2px solid white",
                      borderRadius: "50%",
                      cursor: "ew-resize",
                    zIndex: 3000,
                      pointerEvents: "auto",
                      outline: "none",
                      userSelect: "none",
                    }}
                    onMouseDown={startResizeDrag("w")}
                    title="Resize"
                  />
                </>
              )}
            </div>
          </>
        );
      }

      case "collection-list": {
        return (
          <CollectionListRenderer
            component={component}
            selectedComponent={selectedComponent}
            isPreviewMode={isPreviewMode}
            onComponentClick={(component, _e) => {
              if (selectionEnabled && onComponentSelect) {
                onComponentSelect(component);
              }
            }}
            allComponents={components}
          />
        );
      }

      case "dropdown": {
        return (
          <DropdownRenderer
            component={component}
            baseStyleWithMargin={baseStyleWithMargin}
            handleClick={handleClick}
            handleMouseEnter={handleMouseEnter}
            handleMouseLeave={handleMouseLeave}
            handleMoveMouseDown={handleMoveMouseDown}
          />
        );
      }

      default:
        return null;
    }
  };

  // Filter to only render root components (not nested ones) and sort by zIndex
  const rootComponents = components.filter((c) => !c.parentId).sort((a, b) => b.zIndex - a.zIndex);

  // Use a document-level capture listener to intercept skin component pointer down.
  // pointer-events:none wrappers don't receive native events, so we must go to document level.
  useEffect(() => {
    const handleNativePointerDown = (e: PointerEvent) => {
      if (typeof e.button === "number" && e.button !== 0) return;
      const rawTarget = e.target;
      const targetEl =
        rawTarget instanceof Element
          ? rawTarget
          : rawTarget instanceof Node
            ? rawTarget.parentElement
            : null;
      if (targetEl?.closest("[data-skin-ui]")) return;
      if (targetEl?.classList?.contains("resize-handle")) return;
      if (targetEl?.closest("[data-tabs-control]")) return;

      const x = e.clientX;
      const y = e.clientY;
      const currentPreviewMode = previewModeRef.current;
      console.log("Skin pointerdown raw:", { x, y, isPreviewMode: currentPreviewMode });
      const componentEls = Array.from(
        document.querySelectorAll<HTMLElement>("[data-component-id][data-skin-component]"),
      );
      const candidates = componentEls.filter((el) => {
        const rect = el.getBoundingClientRect();
        if (rect.width <= 0 || rect.height <= 0) return false;
        return x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;
      });

      if (candidates.length === 0) return;

      const pickBest = (elements: HTMLElement[]) => {
        let best = elements[0];
        let bestRect = best.getBoundingClientRect();
        let bestArea = bestRect.width * bestRect.height;
        for (let i = 1; i < elements.length; i += 1) {
          const current = elements[i];
          if (best.contains(current)) {
            best = current;
            bestRect = current.getBoundingClientRect();
            bestArea = bestRect.width * bestRect.height;
            continue;
          }
          if (current.contains(best)) {
            continue;
          }
          const rect = current.getBoundingClientRect();
          const area = rect.width * rect.height;
          if (area < bestArea) {
            best = current;
            bestRect = rect;
            bestArea = area;
          }
        }
        return best;
      };

      const pickedEl = pickBest(candidates);
      const pickedId = pickedEl.dataset.componentId;
      if (!pickedId) return;
      console.log("Skin pointerdown hit:", {
        pickedId,
        isPreviewMode: currentPreviewMode,
        selectionEnabled,
      });
      const component = components.find((c) => c.id === pickedId);
      if (!component) return;

      const isModifierPressed = e.metaKey || e.ctrlKey;
      const shouldTriggerActions = currentPreviewMode || isModifierPressed;
      if (shouldTriggerActions && componentHasActions(component)) {
        const triggered = triggerComponentActions(component, { isModifierPressed, trigger: "click" });
        if (triggered) {
          lastPointerActionRef.current = { componentId: component.id, time: Date.now() };
        }
      }

      if (!selectionEnabled || !onComponentSelect) return;
      onComponentSelect(component);
    };
    document.addEventListener("pointerdown", handleNativePointerDown, { capture: true });
    return () =>
      document.removeEventListener("pointerdown", handleNativePointerDown, { capture: true });
  }, [components, onComponentSelect, selectionEnabled]);

  return (
    <div className="absolute inset-0 pointer-events-none overflow-visible" style={{ zIndex: 20 }}>
      <div
        className="relative w-full h-full pointer-events-none"
        data-skin-root
      >
        {rootComponents.map((component) => (
          <React.Fragment key={component.id}>{renderComponent(component)}</React.Fragment>
        ))}
      </div>
    </div>
  );
};

export default ComponentRenderer;
